import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import Select from "react-select";
import {
  DocumentIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  PencilSquareIcon,
  FolderOpenIcon,
  CheckCircleIcon,
  XMarkIcon,
  PrinterIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { debounce } from "lodash";
import { BASE_URL } from "../../config";
import { openProviderPrintPreview } from "./providerPrintTemplate";

const CONTACT_DEPARTMENT_LABELS = [
  "HMO/Industrial Department",
  "Admitting Department",
  "Emergency Department",
  "Billing Department",
  "Credit & Collection Department",
];

const FIELD_LABEL_MAP = {
  provider_name: "Provider Name",
  short_name: "Short Name",
  licensed_name: "Licensed Name",
  provider_type: "Provider Type",
  phic_datefrom: "PHIC Date From",
  phic_dateto: "PHIC Date To",
  towncity: "Town/City",
  tin: "TIN",
  mef_upload_id: "MEF Upload",
  document_upload_id: "Sign-off Document",
  ppd_tag: "Prompt Payment",
  ppd_rate: "PPD Rate",
  ppd_term: "PPD Term",
  accreditation_date: "Accreditation Date",
  disaccreditation_date: "Disaccreditation Date",
  orientation_date: "Orientation Date",
  orientation_contact_person: "Orientation Contact Person",
  provider_department: "Provider Department",
  contact_person: "Contact Person",
  designation: "Designation",
  mobile_no: "Mobile Number",
  email_add: "Email Address",
  fax_no: "Fax Number",
  landline_no: "Landline Number",
  physician_id: "Physician",
  room_no: "Room Number",
  schedule: "Schedule",
  remarks: "Remarks",
  cpt_code: "CPT Code",
  cpt_rate: "Provider Rate",
  provider_cpt_code: "Provider CPT Code",
  provider_cpt_desc: "Provider CPT Description",
  cpt_desc: "CPT Description",
  cpt_pkg_code: "CPT Package Code",
  maintenance_update_for: "Update For",
  maintenance_effectivity_date: "Effectivity Date",
  maintenance_reason: "Reason",
  maintenance_remarks: "Remarks",
};

const toTitleCaseFromSnake = (value = "") =>
  String(value)
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getFriendlyFieldLabel = (errorKey = "") => {
  const key = String(errorKey || "").trim();
  if (!key) {
    return "Field";
  }

  let match = key.match(/^contact_details\.(\d+)\.([a-z_]+)$/);
  if (match) {
    const rowIndex = Number(match[1]);
    const fieldKey = match[2];
    const departmentLabel =
      CONTACT_DEPARTMENT_LABELS[rowIndex] ||
      `Contact Details Row ${rowIndex + 1}`;
    const fieldLabel =
      FIELD_LABEL_MAP[fieldKey] || toTitleCaseFromSnake(fieldKey);
    return `${departmentLabel} - ${fieldLabel}`;
  }

  match = key.match(/^physician_affiliations\.(\d+)\.([a-z_]+)$/);
  if (match) {
    const rowIndex = Number(match[1]);
    const fieldKey = match[2];
    const fieldLabel =
      FIELD_LABEL_MAP[fieldKey] || toTitleCaseFromSnake(fieldKey);
    return `Physician Affiliation Row ${rowIndex + 1} - ${fieldLabel}`;
  }

  match = key.match(/^cpt_rates\.(\d+)\.([a-z_]+)$/);
  if (match) {
    const rowIndex = Number(match[1]);
    const fieldKey = match[2];
    const fieldLabel =
      FIELD_LABEL_MAP[fieldKey] || toTitleCaseFromSnake(fieldKey);
    return `CPT Row ${rowIndex + 1} - ${fieldLabel}`;
  }

  if (FIELD_LABEL_MAP[key]) {
    return FIELD_LABEL_MAP[key];
  }

  const leafKey = key.split(".").pop() || key;
  return FIELD_LABEL_MAP[leafKey] || toTitleCaseFromSnake(leafKey);
};

const resolveTabFromErrorKey = (errorKey = "") => {
  const key = String(errorKey || "");

  if (key.startsWith("contact_details.")) {
    return "contact_details";
  }
  if (key.startsWith("physician_affiliations")) {
    return "physician_affiliation";
  }
  if (key.startsWith("cpt_rates")) {
    return "cpt";
  }
  if (key.startsWith("signatories") || key.startsWith("designation")) {
    return "signatories";
  }
  if (key.startsWith("internet_") || key.startsWith("wifi_")) {
    return "internet_connectivity";
  }
  if (
    key.startsWith("payment_terms") ||
    key.startsWith("claims_submission") ||
    key.startsWith("ppd_") ||
    key.startsWith("cash_bond") ||
    key.startsWith("late_payment_int") ||
    key.startsWith("tin") ||
    key.startsWith("vat_registered") ||
    key.startsWith("withheld") ||
    key.startsWith("tax_exempt_tag") ||
    key.startsWith("bank_") ||
    key.startsWith("mef_upload_id")
  ) {
    return "payment_terms";
  }
  if (
    key.startsWith("maintenance_") ||
    key === "update_for" ||
    key === "effectivity_date" ||
    key === "maintenance"
  ) {
    return "maintenance";
  }
  if (
    key.startsWith("accreditation_date") ||
    key.startsWith("disaccreditation_date") ||
    key.startsWith("mode_of_release") ||
    key.startsWith("business_hours") ||
    key.startsWith("credit_limit") ||
    key.startsWith("no_of_bed") ||
    key.startsWith("orientation_") ||
    key.startsWith("document_upload_id")
  ) {
    return "miscellaneous";
  }

  return "information";
};

const formatValidationMessage = (message, errorKey = "") => {
  const rawMessage = String(message || "").trim();
  if (!rawMessage) {
    return `${getFriendlyFieldLabel(errorKey)} is invalid.`;
  }

  const selectedMatch = rawMessage.match(/^The selected ([a-z0-9_.]+) is invalid\.?$/i);
  if (selectedMatch) {
    return `Selected ${getFriendlyFieldLabel(selectedMatch[1])} is invalid.`;
  }

  const fieldMatch = rawMessage.match(/^The ([a-z0-9_.]+) field (.+)$/i);
  if (fieldMatch) {
    return `${getFriendlyFieldLabel(fieldMatch[1])} ${fieldMatch[2]}`;
  }

  return rawMessage;
};

const escapeSelectorValue = (value = "") => {
  if (
    typeof window !== "undefined" &&
    window.CSS &&
    typeof window.CSS.escape === "function"
  ) {
    return window.CSS.escape(String(value));
  }

  return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
};

const buildFocusSelectorsForErrorKey = (errorKey = "") => {
  const key = String(errorKey || "").trim();
  if (!key) {
    return [];
  }

  const escapedKey = escapeSelectorValue(key);
  const selectors = [`[data-error-key="${escapedKey}"]`];

  if (!key.includes(".")) {
    selectors.push(`[name="${escapedKey}"]`);
  }

  if (key === "mef_upload_id") {
    selectors.push('[name="mef"]');
  }

  if (key === "document_upload_id") {
    selectors.push('[name="document"]');
  }

  if (key.startsWith("physician_affiliations")) {
    selectors.push('[data-error-key="physician_affiliations"]');
  }

  if (key.startsWith("cpt_rates")) {
    selectors.push('[data-error-key="cpt_rates"]');
  }

  return [...new Set(selectors)];
};

const isEnabledFlag = (value) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  return ["1", "true", "yes", "y", "on"].includes(
    String(value ?? "").trim().toLowerCase()
  );
};

const PPD_HELP_CONTENT = {
  prompt_payment: {
    title: "Prompt Payment",
    points: [
      "Choose Yes when the provider gives a prompt payment discount for faster or early settlement.",
      "When this is Yes, both PPD Rate and PPD Term must be filled in before saving.",
      "Choose No when no prompt payment discount applies. The PPD Rate and PPD Term fields will be cleared and are not required.",
    ],
  },
  ppd_rate: {
    title: "PPD Rate",
    points: [
      "This is the discount percentage applied for prompt payment.",
      "Enter the whole percent number. For example, enter 5 for 5%, not 0.05.",
      "Allowed values are 0.5 up to 20.",
    ],
  },
  ppd_term: {
    title: "PPD Term",
    points: [
      "This tells the system how the prompt payment discount is handled.",
      "Outright means the discount is deducted directly from the provider payment.",
      "Rebate means the discount is tagged as a rebate arrangement instead of a direct outright deduction.",
    ],
  },
};

const PpdHelpButton = ({ helpKey, onClick }) => {
  const help = PPD_HELP_CONTENT[helpKey];

  return (
    <button
      type="button"
      onClick={() => onClick(help)}
      className="inline-flex h-4 w-4 items-center justify-center text-slate-400 hover:text-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
      aria-label={`Show ${help.title} help`}
      title={`Show ${help.title} help`}
    >
      <QuestionMarkCircleIcon className="h-4 w-4" />
    </button>
  );
};

const ClinicSettingExtractClaimsButton = ({ onClick, compact = false }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100 focus:outline-none focus:ring-1 focus:ring-amber-500"
    title="View Clinic Setting Extract Claims details"
  >
    <InformationCircleIcon className="h-3.5 w-3.5" />
    <span>{compact ? "Extract Claims" : "Extract Claims override"}</span>
  </button>
);

const ClinicSettingExtractClaimsModal = ({ onClose }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4"
    role="dialog"
    aria-modal="true"
    aria-labelledby="clinic-setting-extract-claims-title"
  >
    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div>
          <h2
            id="clinic-setting-extract-claims-title"
            className="text-sm font-semibold text-gray-900"
          >
            Clinic Setting Extract Claims Behavior
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            Applies to OUTPATIENT-CLINIC-SETTING during Backend Extract Claims
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
          aria-label="Close Clinic Setting Extract Claims details"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="max-h-[70vh] overflow-y-auto p-4 space-y-4">
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <div className="flex items-start gap-2">
            <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
            <p className="text-xs leading-5 text-amber-900">
              Provider Set-up &gt; Clinic Setting remains editable, but Extract Claims can override this payee setup based on the actual saved claim amounts.
            </p>
          </div>
        </div>

        <div>
          <h3 className="text-xs font-semibold uppercase text-gray-600">
            Layman terms
          </h3>
          <p className="mt-1 text-xs leading-5 text-gray-700">
            For Clinic Setting claims, the payment destination is decided by the claim amount saved for CPT and physicians. If only doctor amount exists, doctors get paid. If only CPT amount exists, the clinic/provider gets paid. If both exist, payment is split.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs font-semibold text-gray-900">Doctor only</p>
            <p className="mt-1 text-xs leading-5 text-gray-700">
              CPT claim amount is 0, physician amount has value. DPR creates PF rows only and the PRN becomes D-...
            </p>
          </div>
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs font-semibold text-gray-900">Split</p>
            <p className="mt-1 text-xs leading-5 text-gray-700">
              CPT claim amount has value and physician amount also has value. DPR creates P-... for provider/clinic and D-... for doctor.
            </p>
          </div>
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs font-semibold text-gray-900">Provider only</p>
            <p className="mt-1 text-xs leading-5 text-gray-700">
              CPT claim amount has value, physician amount is 0. DPR creates the provider/clinic HB row only and the PRN becomes P-...
            </p>
          </div>
        </div>

        <div>
          <h3 className="text-xs font-semibold uppercase text-gray-600">
            Technical details
          </h3>
          <ul className="mt-2 space-y-1 text-xs leading-5 text-gray-700">
            <li>
              Clinic HB amount is sourced from the saved <code>claims_cpt.cpt_rate</code> total.
            </li>
            <li>
              Clinic PF rows are sourced from <code>claims_physician.amount</code> rows greater than 0.
            </li>
            <li>
              Clinic PF rows force <code>transaction_tag = PF</code> and <code>provider_setup = paid to doctor</code> with physician bank details.
            </li>
            <li>Clinic HB rows are generated only when the CPT total is greater than 0.</li>
            <li>This override is applied by Backend Extract Claims. It does not disable Provider Set-up &gt; Clinic Setting.</li>
          </ul>
        </div>

        <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2">
          <p className="text-xs leading-5 text-blue-900">
            Related module: Backend &gt; Extract Claims. The full backend specs are available from the Backend page.
          </p>
        </div>
      </div>

      <div className="border-t border-gray-200 px-4 py-3 flex justify-end gap-2">
        <a
          href="/backend"
          className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          Open Backend
        </a>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center px-3 py-1.5 rounded-md bg-blue-600 text-xs font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          Got it
        </button>
      </div>
    </div>
  </div>
);

const ProviderEnrollmentForm = ({ isViewMode = false }) => {
  const navigate = useNavigate();
  const { providerCode } = useParams();
  const [isEditMode, setIsEditMode] = useState(!!providerCode);
  const [activeTab, setActiveTab] = useState("information");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState({
    region: false,
    province: false,
    towncity: false,
    barangay: false,
    postalcode: false,
  });

  const [formData, setFormData] = useState({
    provider_name: "",
    short_name: "",
    licensed_name: "",
    provider_type: "h",
    phic_datefrom: "",
    phic_dateto: "",
    accreditation_flag: "0",
    remarks: "",
    fraud_flag: false,
    spec_transmittal: false,
    streetno_bldg: "",
    region: "",
    province: "",
    towncity: "",
    barangay: "",
    postalcode: "",
    default_corporate_no_access: false,
    finalize_status: false,
    tin: "",
    tin_from_provider: "",
    tax_exempt_tag: true,
    vat_registered: "0",
    withheld: false,
    bank_acct_name: "",
    bank_acct_no: "",
    bank_id: "",
    bank_branch: "",
    bank_charge: "",
    mef_upload_id: "",
    mef_file_name: "",
    mef_signed_url: "",
    mef_expires_at: "",
    bank_proof_upload_id: "",
    bank_proof_file_name: "",
    bank_proof_signed_url: "",
    bank_proof_expires_at: "",
    payment_terms: "30",
    claims_submission: "30",
    claims_submission2: "30",
    ppd_tag: false,
    ppd_rate: "",
    ppd_term: "0",
    cash_bond: "",
    late_payment_int: "",
    signatories1: "",
    designation1: "",
    signatories2: "",
    designation2: "",
    ip_whole_setup: "0",
    ip_split_hospital: "0",
    ip_split_doctor: "0",
    emergency_whole_setup: "0",
    op_hospital_setup: "0",
    op_doctor_setup: "0",
    lab_hospital_setup: "0",
    clinic_hospital_setup: "0",
    clinic_doctor_setup: "0",
    checkdel_contperson: "",
    checkdel_contno: "",
    checkdel_dep: "",
    mc_flag: true,
    mc_doctor_name: "",
    mc_clinic_hours: "",
    mc_secretary_name: "",
    mc_ip_fee: "",
    mc_op_fee: "",
    mc_specialization: "",
    mc_room_no: "",
    mc_contact_no: "",
    ip_ward_rate: "",
    ip_semi_private_rate: "",
    ip_private_rate: "",
    ip_suite_rate: "",
    ip_icu_rate: "",
    ip_anes_rate: "",
    op_pcp_rate: "",
    op_specialist_rate: "",
    internet_industrial_dept: false,
    internet_billing_dept: false,
    internet_emergency: false,
    wifi_industrial_hmo: false,
    wifi_ip_rooms: false,
    wifi_emergency: false,
    wifi_mab: false,
    payment_setup: "0",
    exclusive_tag: "0",
    accreditation_date: "",
    disaccreditation_date: "",
    mode_of_release: "0",
    business_hours: "",
    credit_limit: "",
    no_of_bed: "",
    orientation_date: "",
    orientation_contact_person: "",
    document_path: "",
    document_upload_id: "",
    document_file_name: "",
    document_signed_url: "",
    document_expires_at: "",
    maintenance_update_for: "",
    maintenance_effectivity_date: "",
    maintenance_reason: "",
    maintenance_remarks: "",
    maintenance_lastupdate_date: "",
    maintenance_update_by: "",
    physician_affiliations: [],
    cpt_rates: [],
    contact_details: [
      {
        provider_department: "HMO/Industrial Department",
        contact_person: "",
        designation: "",
        mobile_no: "",
        email_add: "",
        fax_no: "",
        landline_no: "",
      },
      {
        provider_department: "Admitting Department",
        contact_person: "",
        designation: "",
        mobile_no: "",
        email_add: "",
        fax_no: "",
        landline_no: "",
      },
      {
        provider_department: "Emergency Department",
        contact_person: "",
        designation: "",
        mobile_no: "",
        email_add: "",
        fax_no: "",
        landline_no: "",
      },
      {
        provider_department: "Billing Department",
        contact_person: "",
        designation: "",
        mobile_no: "",
        email_add: "",
        fax_no: "",
        landline_no: "",
      },
      {
        provider_department: "Credit & Collection Department",
        contact_person: "",
        designation: "",
        mobile_no: "",
        email_add: "",
        fax_no: "",
        landline_no: "",
      },
    ],
  });

  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [townCities, setTownCities] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [physicians, setPhysicians] = useState([]);
  const [isPhysicianLoading, setIsPhysicianLoading] = useState(false);
  const [cptOptions, setCptOptions] = useState([]);
  const [isCptLoading, setIsCptLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [banks, setBanks] = useState([]);
  const [initialBankDetails, setInitialBankDetails] = useState(null);
  const [errors, setErrors] = useState({});
  const [pendingErrorFocusKey, setPendingErrorFocusKey] = useState("");
  const [newPhysicianAffiliation, setNewPhysicianAffiliation] = useState({
    physician_id: "",
    room_no: "",
    schedule: "",
    remarks: "",
  });
  const [newCptRate, setNewCptRate] = useState({
    cpt_code: "",
    cpt_description: "",
    cpt_rate: "",
    provider_cpt_code: "",
  });
  const providerCptFileInputRef = useRef(null);
  const [providerCptRows, setProviderCptRows] = useState([]);
  const [isProviderCptLoading, setIsProviderCptLoading] = useState(false);
  const [providerCptFile, setProviderCptFile] = useState(null);
  const [providerCptPreview, setProviderCptPreview] = useState(null);
  const [isProviderCptSaving, setIsProviderCptSaving] = useState(false);
  const [isProviderCptPreviewing, setIsProviderCptPreviewing] = useState(false);
  const [isProviderCptCommitting, setIsProviderCptCommitting] = useState(false);
  const [providerCptModalMode, setProviderCptModalMode] = useState("");
  const [providerCptForm, setProviderCptForm] = useState({
    master_cpt_code: "",
    master_cpt_desc: "",
    provider_cpt_code: "",
    cpt_desc: "",
    cpt_rate: "",
    cpt_source: "",
  });
  const [physicianAffiliationSearchQuery, setPhysicianAffiliationSearchQuery] =
    useState("");
  const [physicianAffiliationCurrentPage, setPhysicianAffiliationCurrentPage] =
    useState(1);
  const [
    physicianAffiliationItemsPerPage,
    setPhysicianAffiliationItemsPerPage,
  ] = useState(10);
  const [cptSearchQuery, setCptSearchQuery] = useState("");
  const [cptCurrentPage, setCptCurrentPage] = useState(1);
  const [cptItemsPerPage, setCptItemsPerPage] = useState(10);
  const [isExportingPhysician, setIsExportingPhysician] = useState(false);
  const [isExportingCpt, setIsExportingCpt] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [ppdHelpContent, setPpdHelpContent] = useState(null);
  const [
    showClinicSettingExtractClaimsInfo,
    setShowClinicSettingExtractClaimsInfo,
  ] = useState(false);
  const [showMaintenanceConfirmation, setShowMaintenanceConfirmation] = useState(false);
  const [pendingMaintenancePayload, setPendingMaintenancePayload] = useState(null);

  // Add new state to track if the form has changes
  const [isFormDirty, setIsFormDirty] = useState(false);

  // Add useRef to store initial form data for comparison
  const initialFormData = useRef(formData);

  // Add state to track previous location
  const [previousLocation, setPreviousLocation] = useState(null);

  const location = useLocation(); // Explicitly get location from useLocation
  const isViewRoute = location.pathname.startsWith("/medical/view-provider/");
  const isReadOnlyMode = isViewMode || isViewRoute;
  const isFormLocked = isLoading || isReadOnlyMode;
  const useProviderCptApi = Boolean(providerCode);

  const normalizeBackendErrors = useCallback((backendErrors = {}) => {
    const normalizedErrors = {};
    const formattedMessages = [];
    let firstErrorKey = "";

    Object.entries(backendErrors).forEach(([key, value]) => {
      const rawMessages = Array.isArray(value) ? value : [value];
      const cleanedMessages = rawMessages
        .map((message) => formatValidationMessage(message, key))
        .filter((message) => String(message || "").trim() !== "");

      if (cleanedMessages.length > 0) {
        normalizedErrors[key] = cleanedMessages;
        formattedMessages.push(...cleanedMessages);
        if (!firstErrorKey) {
          firstErrorKey = key;
        }
      }
    });

    return {
      normalizedErrors,
      formattedMessages: [...new Set(formattedMessages)],
      firstErrorKey,
    };
  }, []);

  const preparePaginationForErrorKey = useCallback(
    (errorKey = "") => {
      const key = String(errorKey || "");

      let match = key.match(/^physician_affiliations\.(\d+)\./);
      if (match) {
        const rowIndex = Number(match[1]);
        setPhysicianAffiliationSearchQuery("");
        setPhysicianAffiliationCurrentPage(
          Math.floor(rowIndex / physicianAffiliationItemsPerPage) + 1
        );
        return;
      }

      match = key.match(/^cpt_rates\.(\d+)\./);
      if (match) {
        const rowIndex = Number(match[1]);
        setCptSearchQuery("");
        setCptCurrentPage(Math.floor(rowIndex / cptItemsPerPage) + 1);
      }
    },
    [physicianAffiliationItemsPerPage, cptItemsPerPage]
  );

  const getAuthHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
    }),
    []
  );

  const syncProviderCptRowsToForm = useCallback((rows = []) => {
    const persistedProviderRows = rows
      .filter((row) => row.rate_id)
      .map((row) => ({
        cpt_code: row.master_cpt_code || row.cpt_code || "",
        cpt_rate: row.cpt_rate || "",
        provider_cpt_code: row.provider_cpt_code || "",
        provider_cpt_desc: row.cpt_desc || "",
        cpt_rate_major: row.cpt_rate_major ?? null,
        cpt_pkg_code: row.cpt_pkg_code ?? null,
      }));

    setFormData((prev) => {
      const packageRows = prev.cpt_rates.filter(
        (rate) => String(rate.cpt_pkg_code || "").trim() !== ""
      );

      return {
        ...prev,
        cpt_rates: [...packageRows, ...persistedProviderRows],
      };
    });
  }, []);

  const loadProviderCptList = useCallback(async () => {
    if (!providerCode) {
      setProviderCptRows([]);
      return;
    }

    try {
      setIsProviderCptLoading(true);
      const response = await axios.get(
        `${BASE_URL}/providers/${providerCode}/cpt-list`,
        {
          headers: getAuthHeaders(),
        }
      );
      const rows = response.data?.data?.cpt_rates || [];
      setProviderCptRows(rows);
      syncProviderCptRowsToForm(rows);
    } catch (error) {
      console.error("Error fetching provider CPT list:", error);
      toast.error("Failed to load provider CPT list.", { autoClose: 3000 });
    } finally {
      setIsProviderCptLoading(false);
    }
  }, [providerCode, getAuthHeaders, syncProviderCptRowsToForm]);

  useEffect(() => {
    if (!pendingErrorFocusKey) {
      return undefined;
    }

    const timerId = setTimeout(() => {
      const selectors = buildFocusSelectorsForErrorKey(pendingErrorFocusKey);
      for (const selector of selectors) {
        const targetElement = document.querySelector(selector);
        if (!targetElement) {
          continue;
        }

        if (typeof targetElement.scrollIntoView === "function") {
          targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }

        if (typeof targetElement.focus === "function") {
          targetElement.focus({ preventScroll: true });
        }

        break;
      }

      setPendingErrorFocusKey("");
    }, 80);

    return () => clearTimeout(timerId);
  }, [
    pendingErrorFocusKey,
    activeTab,
    formData.contact_details.length,
    formData.physician_affiliations.length,
    formData.cpt_rates.length,
  ]);

  useEffect(() => {
    if (
      isFormDirty &&
      previousLocation &&
      previousLocation !== location.pathname
    ) {
      setShowConfirmation(true);
      setNextLocation(previousLocation);
    }
  }, [isFormDirty, previousLocation, location.pathname]);

  // Fetch banks
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${BASE_URL}/bank/get-banks`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        });
        setBanks(response.data.data.banks);
      } catch (error) {
        console.error("Error fetching banks:", error);
        toast.error("Failed to load banks.", { autoClose: 3000 });
      } finally {
        setIsLoading(false);
      }
    };
    fetchBanks();
  }, []);

  // Fetch provider data for edit mode with loading
  useEffect(() => {
    if (isEditMode) {
      const fetchProvider = async () => {
        try {
          setIsLoading(true);
          const response = await axios.get(
            `${BASE_URL}/providers/${providerCode}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "authentication/token"
                )}`,
              },
            }
          );
          const {
            provider,
            professional_fees,
            connection,
            contact_details,
            payment_setup,
            physician_affiliations,
            cpt_rates,
          } = response.data.data;

          let maintenance = {};
          try {
            const maintenanceResponse = await axios.get(
              `${BASE_URL}/providers/${providerCode}/maintenance`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem(
                    "authentication/token"
                  )}`,
                },
              }
            );
            maintenance = maintenanceResponse.data?.data?.maintenance || {};
          } catch (maintenanceError) {
            console.error("Error fetching provider maintenance:", maintenanceError);
          }

          setInitialBankDetails({
            bank_acct_name: provider.bank_acct_name || "",
            bank_acct_no: provider.bank_acct_no || "",
            bank_id: provider.bank_id || "",
            bank_branch: provider.bank_branch || "",
          });

          const providerPromptPaymentEnabled = isEnabledFlag(provider.ppd_tag);

          setFormData((prev) => ({
            ...prev,
            ...provider,
            ...professional_fees,
            ...connection,
            ...payment_setup,
            ppd_tag: providerPromptPaymentEnabled,
            ppd_rate: providerPromptPaymentEnabled
              ? provider.ppd_rate ?? ""
              : "",
            ppd_term: providerPromptPaymentEnabled
              ? String(provider.ppd_term ?? "0")
              : "0",
            contact_details:
              contact_details.length > 0
                ? contact_details
                : prev.contact_details,
            physician_affiliations:
              physician_affiliations.length > 0
                ? physician_affiliations
                : prev.physician_affiliations,
            cpt_rates: cpt_rates.length > 0 ? cpt_rates : prev.cpt_rates,
            business_hours: provider.schedule || "",
            vat_registered:
              provider.vat_registered !== null &&
              provider.vat_registered !== undefined
                ? String(provider.vat_registered)
                : "0",
            maintenance_update_for: maintenance.update_for || "",
            maintenance_effectivity_date: maintenance.effectivity_date || "",
            maintenance_reason: maintenance.reason || "",
            maintenance_remarks: maintenance.remarks || "",
            maintenance_lastupdate_date: maintenance.lastupdate_date || "",
            maintenance_update_by: maintenance.update_by || "",
          }));

          await loadProviderCptList();
        } catch (error) {
          console.error("Error fetching provider:", error);
          toast.error("Failed to load provider data.", { autoClose: 3000 });
        } finally {
          setIsLoading(false);
        }
      };
      fetchProvider();
    }
  }, [providerCode, isEditMode, loadProviderCptList]);

  // Sequential address loading
  // Fetch regions
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        setIsLoadingAddress((prev) => ({ ...prev, region: true }));
        const response = await axios.get(
          `${BASE_URL}/address/get-ref-address`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }
        );
        setRegions(response.data.data);
      } catch (error) {
        console.error("Error fetching regions:", error);
        toast.error("Failed to load regions.", { autoClose: 3000 });
      } finally {
        setIsLoadingAddress((prev) => ({ ...prev, region: false }));
      }
    };
    fetchRegions();
  }, []);

  // Fetch provinces after region is selected
  useEffect(() => {
    if (formData.region) {
      const fetchProvinces = async () => {
        try {
          setIsLoadingAddress((prev) => ({ ...prev, province: true }));
          const response = await axios.get(
            `${BASE_URL}/address/get-ref-address`,
            {
              params: { region_code: formData.region },
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "authentication/token"
                )}`,
              },
            }
          );
          setProvinces(response.data.data);
          if (!isEditMode) {
            setFormData((prev) => ({
              ...prev,
              province: "",
              towncity: "",
              barangay: "",
              postalcode: "",
            }));
          }
        } catch (error) {
          console.error("Error fetching provinces:", error);
          toast.error("Failed to load provinces.", { autoClose: 3000 });
        } finally {
          setIsLoadingAddress((prev) => ({ ...prev, province: false }));
        }
      };
      fetchProvinces();
    } else {
      setProvinces([]);
      setTownCities([]);
      setBarangays([]);
      if (!isEditMode) {
        setFormData((prev) => ({
          ...prev,
          province: "",
          towncity: "",
          barangay: "",
          postalcode: "",
        }));
      }
    }
  }, [formData.region, isEditMode]);

  // Fetch town cities after province is selected
  useEffect(() => {
    if (formData.province && !isLoadingAddress.province) {
      const fetchTownCities = async () => {
        try {
          setIsLoadingAddress((prev) => ({ ...prev, towncity: true }));
          const response = await axios.get(
            `${BASE_URL}/address/get-ref-address`,
            {
              params: {
                region_code: formData.region,
                province_code: formData.province,
              },
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "authentication/token"
                )}`,
              },
            }
          );
          setTownCities(response.data.data);
          if (!isEditMode) {
            setFormData((prev) => ({
              ...prev,
              towncity: "",
              barangay: "",
              postalcode: "",
            }));
          }
        } catch (error) {
          console.error("Error fetching town cities:", error);
          toast.error("Failed to load town cities.", { autoClose: 3000 });
        } finally {
          setIsLoadingAddress((prev) => ({ ...prev, towncity: false }));
        }
      };
      fetchTownCities();
    } else {
      setTownCities([]);
      setBarangays([]);
      if (!isEditMode) {
        setFormData((prev) => ({
          ...prev,
          towncity: "",
          barangay: "",
          postalcode: "",
        }));
      }
    }
  }, [formData.province, isLoadingAddress.province, isEditMode]);

  // Fetch barangays after town city is selected
  useEffect(() => {
    if (formData.towncity && !isLoadingAddress.towncity) {
      const fetchBarangays = async () => {
        try {
          setIsLoadingAddress((prev) => ({ ...prev, barangay: true }));
          const response = await axios.get(
            `${BASE_URL}/address/get-ref-address`,
            {
              params: {
                region_code: formData.region,
                province_code: formData.province,
                city_code: formData.towncity,
              },
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "authentication/token"
                )}`,
              },
            }
          );
          setBarangays(response.data.data);
          if (!isEditMode) {
            setFormData((prev) => ({ ...prev, barangay: "", postalcode: "" }));
          }
        } catch (error) {
          console.error("Error fetching barangays:", error);
          toast.error("Failed to load barangays.", { autoClose: 3000 });
        } finally {
          setIsLoadingAddress((prev) => ({ ...prev, barangay: false }));
        }
      };
      fetchBarangays();
    } else {
      setBarangays([]);
      if (!isEditMode) {
        setFormData((prev) => ({ ...prev, barangay: "", postalcode: "" }));
      }
    }
  }, [formData.towncity, isLoadingAddress.towncity, isEditMode]);

  // Fetch postal code after barangay is selected
  useEffect(() => {
    if (formData.barangay && !isLoadingAddress.barangay) {
      const selectedBarangay = barangays.find(
        (item) => item.baranggay_code === formData.barangay
      );
      setFormData((prev) => ({
        ...prev,
        postalcode: selectedBarangay?.postcode || "",
      }));
    } else if (!isEditMode) {
      setFormData((prev) => ({ ...prev, postalcode: "" }));
    }
  }, [formData.barangay, barangays, isLoadingAddress.barangay, isEditMode]);

  // Fetch physicians
  const fetchPhysicians = async (search = "") => {
    try {
      setIsPhysicianLoading(true);
      const response = await axios.get(
        `${BASE_URL}/physicians/get-physicians`,
        {
          params: { search, limit: 50 },
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      setPhysicians(response.data.data.physicians);
    } catch (error) {
      console.error("Error fetching physicians:", error);
      toast.error("Failed to load physicians.", { autoClose: 3000 });
    } finally {
      setIsPhysicianLoading(false);
    }
  };

  // Fetch CPT codes
  const fetchCptCodes = async (search = "") => {
    try {
      setIsCptLoading(true);
      const response = await axios.get(`${BASE_URL}/cpt/get-cpt-codes`, {
        params: { search, limit: 50 },
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      });
      setCptOptions(response.data.data.cpt_codes);
    } catch (error) {
      console.error("Error fetching CPT codes:", error);
      toast.error("Failed to load CPT codes.", { autoClose: 3000 });
    } finally {
      setIsCptLoading(false);
    }
  };

  // Fetch providers for dropdown
  const fetchProviders = async (search = "") => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${BASE_URL}/providers/list/for-dropdown-select`,
        {
          params: { search, limit: 50 },
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      setProviders(response.data.data.providers);
    } catch (error) {
      console.error("Error fetching providers:", error);
      toast.error("Failed to load providers.", { autoClose: 3000 });
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedFetchCptCodes = debounce(fetchCptCodes, 300);
  const debouncedFetchPhysicians = debounce(fetchPhysicians, 300);
  const debouncedFetchProviders = debounce(fetchProviders, 300);

  useEffect(() => {
    fetchPhysicians();
    fetchCptCodes();
    fetchProviders();
  }, []);

  const handleInputChange = (e, index = null, field = null, section = null) => {
    const { name, value, type, checked, files } = e.target;

    if (section === "physician_affiliations") {
      const updatedAffiliations = [...formData.physician_affiliations];
      updatedAffiliations[index][field] = value;
      setFormData({ ...formData, physician_affiliations: updatedAffiliations });
    } else if (section === "cpt_rates") {
      const updatedCptRates = [...formData.cpt_rates];
      updatedCptRates[index][field] = value;
      setFormData({ ...formData, cpt_rates: updatedCptRates });
    } else if (index !== null && field) {
      const updatedContactDetails = [...formData.contact_details];
      updatedContactDetails[index][field] = value;
      setFormData({ ...formData, contact_details: updatedContactDetails });
    } else {
      let newValue;
      if (type === "checkbox") {
        newValue = checked;
      } else if (type === "file") {
        newValue = files[0];
      } else if (
        type === "radio" &&
        (name === "tax_exempt_tag" || name === "ppd_tag" || name === "mc_flag")
      ) {
        newValue = value === "true";
      } else {
        newValue = value;
      }

      const nextFormData = {
        ...formData,
        [name]: newValue,
      };

      if (name === "ppd_tag" && newValue === false) {
        nextFormData.ppd_rate = "";
        nextFormData.ppd_term = "0";
      }

      setFormData(nextFormData);
    }

    if (["ppd_tag", "ppd_rate", "ppd_term"].includes(name)) {
      setErrors((prevErrors) => {
        const nextErrors = { ...prevErrors };
        delete nextErrors[name];

        if (name === "ppd_tag") {
          delete nextErrors.ppd_rate;
          delete nextErrors.ppd_term;
        }

        return nextErrors;
      });
    }

    setIsFormDirty(true); // Mark form as dirty on any change
  };

  const handlePhysicianSelect = (selectedOption) => {
    setNewPhysicianAffiliation((prev) => ({
      ...prev,
      physician_id: selectedOption ? selectedOption.value : "",
      full_name: selectedOption ? selectedOption.label : "",
      specialization: selectedOption?.specialization || "",
      sub_specialization: selectedOption?.sub_specialization || "",
    }));
    setIsFormDirty(true);
  };

  const handleCptSelect = (selectedOption) => {
    setNewCptRate((prev) => ({
      ...prev,
      cpt_code: selectedOption ? selectedOption.value : "",
      cpt_description: selectedOption ? selectedOption.description : "",
    }));
    setIsFormDirty(true);
  };

  const handleNewPhysicianAffiliationChange = (e) => {
    const { name, value } = e.target;
    setNewPhysicianAffiliation((prev) => ({
      ...prev,
      [name]: value,
    }));
    setIsFormDirty(true);
  };

  const handleNewCptRateChange = (e) => {
    const { name, value } = e.target;
    setNewCptRate((prev) => ({
      ...prev,
      [name]: value,
    }));
    setIsFormDirty(true);
  };

  const handlePhysicianSearch = (inputValue, actionMeta) => {
    if (actionMeta?.action !== "input-change") {
      return inputValue;
    }

    if (inputValue.length >= 2) {
      debouncedFetchPhysicians(inputValue);
    } else if (inputValue.length === 0) {
      debouncedFetchPhysicians();
    }

    return inputValue;
  };

  const handleCptSearch = (inputValue, actionMeta) => {
    if (actionMeta?.action !== "input-change") {
      return inputValue;
    }

    if (inputValue.length >= 2) {
      debouncedFetchCptCodes(inputValue);
    } else if (inputValue.length === 0) {
      debouncedFetchCptCodes();
    }

    return inputValue;
  };

  const handleProviderSelect = (selectedOption) => {
    setFormData({
      ...formData,
      tin_from_provider: selectedOption ? selectedOption.value : "",
      tin: selectedOption ? selectedOption.tin : "",
    });

    setIsFormDirty(true);
  };

  const handleTinChange = (e) => {
    setFormData({
      ...formData,
      tin: e.target.value,
      tin_from_provider: "",
    });

    setIsFormDirty(true);
  };

  const handleProviderSearch = (inputValue) => {
    if (inputValue.length >= 2) {
      debouncedFetchProviders(inputValue);
    } else if (inputValue.length === 0) {
      debouncedFetchProviders();
    }
  };

  const normalizeBankText = (value) => String(value ?? "").trim();
  const normalizeBankAccountNo = (value) => normalizeBankText(value).replace(/[\s-]/g, "");
  const normalizeBankId = (value) => normalizeBankText(value);
  const splitCommaSeparatedValues = (value) => {
    const rawValue = String(value ?? "").trim();
    if (!rawValue) {
      return [];
    }

    return rawValue.split(",").map((item) => item.trim());
  };
  const buildInvalidMultiValueMessage = (label, invalidValue) => {
    if (invalidValue === "") {
      return `Invalid ${label} format. Empty value detected between commas.`;
    }

    return `Invalid ${label} format: ${invalidValue}.`;
  };

  const hasValidPhoneFormatting = (phoneRaw) => {
    if (/[^0-9+\-\s()]/.test(phoneRaw)) {
      return false;
    }

    const plusCount = (phoneRaw.match(/\+/g) || []).length;
    if (plusCount > 1) {
      return false;
    }
    if (phoneRaw.includes("+") && !phoneRaw.startsWith("+")) {
      return false;
    }

    const openParenCount = (phoneRaw.match(/\(/g) || []).length;
    const closeParenCount = (phoneRaw.match(/\)/g) || []).length;
    if (openParenCount !== closeParenCount) {
      return false;
    }

    let parenDepth = 0;
    for (const char of phoneRaw) {
      if (char === "(") {
        parenDepth += 1;
      } else if (char === ")") {
        parenDepth -= 1;
        if (parenDepth < 0) {
          return false;
        }
      }
    }

    return parenDepth === 0;
  };

  const getPhoneNumberWithoutExtension = (phoneRaw) => {
    const rawValue = String(phoneRaw ?? "").trim();
    const extensionMatch = rawValue.match(
      /^(.*?)(?:\s*(?:ext\.?|extension|local|loc\.?|x)\s*\d{1,6})$/i
    );

    return (extensionMatch ? extensionMatch[1] : rawValue).trim();
  };

  const compactPhoneNumber = (phoneRaw) =>
    String(phoneRaw ?? "").replace(/[\s\-()]/g, "");

  const normalizeMobileNumber = (mobile) => {
    const mobileRaw = String(mobile ?? "").trim();
    if (!mobileRaw) {
      return null;
    }

    if (!hasValidPhoneFormatting(mobileRaw)) {
      return null;
    }

    const compactMobile = compactPhoneNumber(mobileRaw);
    if (!compactMobile) {
      return null;
    }

    if (/^\+63[89]\d{9}$/.test(compactMobile)) {
      return `0${compactMobile.slice(3)}`;
    }
    if (/^0063[89]\d{9}$/.test(compactMobile)) {
      return `0${compactMobile.slice(4)}`;
    }
    if (/^63[89]\d{9}$/.test(compactMobile)) {
      return `0${compactMobile.slice(2)}`;
    }

    if (/^[89]\d{9}$/.test(compactMobile)) {
      return `0${compactMobile}`;
    }
    if (/^0[89]\d{9}$/.test(compactMobile)) {
      return compactMobile;
    }

    return null;
  };

  const isValidEmailAddress = (email) => {
    const emailValue = String(email ?? "").trim();
    if (!emailValue || emailValue.length > 254) {
      return false;
    }

    const emailParts = emailValue.split("@");
    if (emailParts.length !== 2) {
      return false;
    }

    const [localPart, domainPart] = emailParts;
    if (
      !localPart ||
      !domainPart ||
      localPart.length > 64 ||
      localPart.startsWith(".") ||
      localPart.endsWith(".") ||
      localPart.includes("..")
    ) {
      return false;
    }

    if (!/^[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/i.test(localPart)) {
      return false;
    }

    const domainLabels = domainPart.split(".");
    if (domainLabels.length < 2) {
      return false;
    }

    return domainLabels.every(
      (label) =>
        /^[A-Z0-9-]+$/i.test(label) &&
        label.length >= 1 &&
        label.length <= 63 &&
        !label.startsWith("-") &&
        !label.endsWith("-")
    );
  };

  const isValidPhilippineLandlineDigits = (phoneDigits) => {
    if (/^(?:1800|00800)\d{7,9}$/.test(phoneDigits)) {
      return true;
    }
    if (/^(?:\+63|0063)(?:2\d{8}|[3-8]\d{8})$/.test(phoneDigits)) {
      return true;
    }
    if (/^63(?:2\d{8}|[3-8]\d{8})$/.test(phoneDigits)) {
      return true;
    }
    if (/^02\d{8}$/.test(phoneDigits)) {
      return true;
    }
    if (/^0[3-8]\d{8}$/.test(phoneDigits)) {
      return true;
    }
    if (/^2\d{8}$/.test(phoneDigits)) {
      return true;
    }
    if (/^[3-8]\d{8}$/.test(phoneDigits)) {
      return true;
    }

    return /^\d{7,8}$/.test(phoneDigits);
  };

  const isValidLandlineOrFaxNumber = (number) => {
    const numberRaw = String(number ?? "").trim();
    if (!numberRaw) {
      return true;
    }

    const phoneWithoutExtension = getPhoneNumberWithoutExtension(numberRaw);
    if (!phoneWithoutExtension || !hasValidPhoneFormatting(phoneWithoutExtension)) {
      return false;
    }

    const phoneDigits = compactPhoneNumber(phoneWithoutExtension);
    return isValidPhilippineLandlineDigits(phoneDigits);
  };

  const validateAndNormalizeContactDetails = (contactDetails = []) => {
    const localErrors = {};
    const normalizedContactDetails = contactDetails.map((contact = {}, index) => {
      const normalizedContact = { ...contact };

      const mobileRaw = String(contact.mobile_no ?? "").trim();
      if (mobileRaw) {
        const mobileValues = splitCommaSeparatedValues(mobileRaw);
        const normalizedMobileValues = [];
        let invalidMobileValue = null;

        for (const mobileValue of mobileValues) {
          if (!mobileValue) {
            invalidMobileValue = "";
            break;
          }

          const normalizedMobile = normalizeMobileNumber(mobileValue);
          if (!normalizedMobile) {
            invalidMobileValue = mobileValue;
            break;
          }

          normalizedMobileValues.push(normalizedMobile);
        }

        if (invalidMobileValue !== null) {
          localErrors[`contact_details.${index}.mobile_no`] = [
            buildInvalidMultiValueMessage("mobile number", invalidMobileValue),
          ];
        } else {
          normalizedContact.mobile_no = normalizedMobileValues.join(", ");
        }
      } else {
        normalizedContact.mobile_no = "";
      }

      const emailRaw = String(contact.email_add ?? "").trim();
      if (emailRaw) {
        const emailValues = splitCommaSeparatedValues(emailRaw);
        const normalizedEmailValues = [];
        let invalidEmailValue = null;

        for (const emailValue of emailValues) {
          if (!emailValue) {
            invalidEmailValue = "";
            break;
          }

          if (!isValidEmailAddress(emailValue)) {
            invalidEmailValue = emailValue;
            break;
          }

          normalizedEmailValues.push(emailValue);
        }

        if (invalidEmailValue !== null) {
          localErrors[`contact_details.${index}.email_add`] = [
            buildInvalidMultiValueMessage("email address", invalidEmailValue),
          ];
        } else {
          normalizedContact.email_add = normalizedEmailValues.join(", ");
        }
      } else {
        normalizedContact.email_add = "";
      }

      const faxRaw = String(contact.fax_no ?? "").trim();
      if (faxRaw) {
        const faxValues = splitCommaSeparatedValues(faxRaw);
        const normalizedFaxValues = [];
        let invalidFaxValue = null;

        for (const faxValue of faxValues) {
          if (!faxValue) {
            invalidFaxValue = "";
            break;
          }

          if (!isValidLandlineOrFaxNumber(faxValue)) {
            invalidFaxValue = faxValue;
            break;
          }

          normalizedFaxValues.push(faxValue);
        }

        if (invalidFaxValue !== null) {
          localErrors[`contact_details.${index}.fax_no`] = [
            buildInvalidMultiValueMessage("fax number", invalidFaxValue),
          ];
        } else {
          normalizedContact.fax_no = normalizedFaxValues.join(", ");
        }
      } else {
        normalizedContact.fax_no = "";
      }

      const landlineRaw = String(contact.landline_no ?? "").trim();
      if (landlineRaw) {
        const landlineValues = splitCommaSeparatedValues(landlineRaw);
        const normalizedLandlineValues = [];
        let invalidLandlineValue = null;

        for (const landlineValue of landlineValues) {
          if (!landlineValue) {
            invalidLandlineValue = "";
            break;
          }

          if (!isValidLandlineOrFaxNumber(landlineValue)) {
            invalidLandlineValue = landlineValue;
            break;
          }

          normalizedLandlineValues.push(landlineValue);
        }

        if (invalidLandlineValue !== null) {
          localErrors[`contact_details.${index}.landline_no`] = [
            buildInvalidMultiValueMessage("landline number", invalidLandlineValue),
          ];
        } else {
          normalizedContact.landline_no = normalizedLandlineValues.join(", ");
        }
      } else {
        normalizedContact.landline_no = "";
      }

      return normalizedContact;
    });

    return { localErrors, normalizedContactDetails };
  };

  const maintenanceFieldKeyMap = {
    update_for: "maintenance_update_for",
    effectivity_date: "maintenance_effectivity_date",
    reason: "maintenance_reason",
    remarks: "maintenance_remarks",
  };

  const buildMaintenancePayload = () => ({
    update_for: String(formData.maintenance_update_for || "")
      .trim()
      .toLowerCase(),
    effectivity_date: String(formData.maintenance_effectivity_date || "").trim(),
    reason: String(formData.maintenance_reason || "").trim(),
    remarks: String(formData.maintenance_remarks || "").trim(),
    tin: String(formData.tin || "").trim(),
    tin_from_provider: String(formData.tin_from_provider || "").trim(),
  });

  const validateMaintenancePayload = (payload) => {
    const localErrors = {};

    if (!payload.update_for) {
      localErrors.maintenance_update_for = ["Update For is required."];
    }

    if (!payload.effectivity_date) {
      localErrors.maintenance_effectivity_date = ["Effectivity Date is required."];
    } else if (Number.isNaN(new Date(payload.effectivity_date).getTime())) {
      localErrors.maintenance_effectivity_date = ["Effectivity Date must be a valid date."];
    }

    if (!payload.reason) {
      localErrors.maintenance_reason = ["Reason is required."];
    }

    return localErrors;
  };

  const remapMaintenanceErrors = (backendErrors = {}) => {
    const remapped = {};

    Object.entries(backendErrors || {}).forEach(([key, value]) => {
      const mappedKey = maintenanceFieldKeyMap[key] || key;
      remapped[mappedKey] = value;
    });

    return remapped;
  };

  const handleMaintenanceConflictError = (errorData = {}) => {
    const baseMessage = String(errorData.error || "TIN conflict detected.").trim();
    const providerCodeConflict = String(errorData.provider_code || "").trim();
    const physicianFullname = String(errorData.fullname || "").trim();

    let tinMessage = baseMessage;
    if (providerCodeConflict) {
      tinMessage = `${baseMessage} Provider ${providerCodeConflict} record found.`;
    } else if (physicianFullname) {
      tinMessage = `${baseMessage} Physician ${physicianFullname} record found.`;
    }

    setErrors({
      tin: [tinMessage],
    });
    setActiveTab("payment_terms");
    setPendingErrorFocusKey("tin");
    toast.error(tinMessage, { autoClose: 4500 });
  };

  const applyMaintenanceUpdate = async (payload) => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${BASE_URL}/providers/${providerCode}/maintenance`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );

      const maintenanceData = response?.data?.data?.maintenance || {};
      setFormData((prev) => ({
        ...prev,
        maintenance_update_for:
          maintenanceData.update_for ?? prev.maintenance_update_for,
        maintenance_effectivity_date:
          maintenanceData.effectivity_date ?? prev.maintenance_effectivity_date,
        maintenance_reason: maintenanceData.reason ?? prev.maintenance_reason,
        maintenance_remarks: maintenanceData.remarks ?? prev.maintenance_remarks,
        maintenance_lastupdate_date:
          maintenanceData.lastupdate_date ?? prev.maintenance_lastupdate_date,
        maintenance_update_by:
          maintenanceData.update_by ?? prev.maintenance_update_by,
      }));

      toast.success(
        response?.data?.message || "Provider maintenance updated successfully.",
        {
          autoClose: 3000,
        }
      );
      setIsFormDirty(false);
      setErrors({});
      setPendingErrorFocusKey("");
    } catch (error) {
      if (error.response?.status === 422) {
        const remappedErrors = remapMaintenanceErrors(
          error.response?.data?.errors || {}
        );
        const { normalizedErrors, formattedMessages, firstErrorKey } =
          normalizeBackendErrors(remappedErrors);

        setErrors(normalizedErrors);

        if (firstErrorKey) {
          setActiveTab(resolveTabFromErrorKey(firstErrorKey));
          setPendingErrorFocusKey(firstErrorKey);
        }

        toast.error(
          formattedMessages.join(" ") || "Please fix the highlighted fields.",
          { autoClose: 5000 }
        );
      } else if (error.response?.status === 409) {
        handleMaintenanceConflictError(error.response?.data || {});
      } else {
        toast.error(
          error.response?.data?.message ||
            "An error occurred while saving maintenance update.",
          { autoClose: 3000 }
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMaintenanceSubmit = async () => {
    if (!providerCode) {
      toast.error("Maintenance update is available only for existing providers.", {
        autoClose: 3000,
      });
      return;
    }

    setErrors({});
    setPendingErrorFocusKey("");

    const maintenancePayload = buildMaintenancePayload();
    const localErrors = validateMaintenancePayload(maintenancePayload);
    if (Object.keys(localErrors).length > 0) {
      const firstErrorKey = Object.keys(localErrors)[0] || "";
      setErrors(localErrors);
      setActiveTab("maintenance");
      setPendingErrorFocusKey(firstErrorKey);
      toast.error(localErrors[firstErrorKey]?.[0] || "Please complete required fields.", {
        autoClose: 4000,
      });
      return;
    }

    try {
      setIsLoading(true);
      await axios.post(
        `${BASE_URL}/providers/${providerCode}/maintenance/check-tin`,
        {
          tin: maintenancePayload.tin,
          tin_from_provider: maintenancePayload.tin_from_provider,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );

      setPendingMaintenancePayload(maintenancePayload);
      setShowMaintenanceConfirmation(true);
    } catch (error) {
      if (error.response?.status === 422) {
        const remappedErrors = remapMaintenanceErrors(
          error.response?.data?.errors || {}
        );
        const { normalizedErrors, formattedMessages, firstErrorKey } =
          normalizeBackendErrors(remappedErrors);

        setErrors(normalizedErrors);
        if (firstErrorKey) {
          setActiveTab(resolveTabFromErrorKey(firstErrorKey));
          setPendingErrorFocusKey(firstErrorKey);
        }

        toast.error(
          formattedMessages.join(" ") || "Please fix the highlighted fields.",
          { autoClose: 5000 }
        );
      } else if (error.response?.status === 409) {
        handleMaintenanceConflictError(error.response?.data || {});
      } else {
        toast.error(
          error.response?.data?.message ||
            "Failed to validate maintenance request.",
          { autoClose: 3000 }
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const providerBankChanged = () => {
    if (!isEditMode || !initialBankDetails) return false;
    return (
      normalizeBankText(initialBankDetails.bank_acct_name) !== normalizeBankText(formData.bank_acct_name) ||
      normalizeBankAccountNo(initialBankDetails.bank_acct_no) !== normalizeBankAccountNo(formData.bank_acct_no) ||
      normalizeBankText(initialBankDetails.bank_branch) !== normalizeBankText(formData.bank_branch) ||
      normalizeBankId(initialBankDetails.bank_id) !== normalizeBankId(formData.bank_id)
    );
  };

  const validatePromptPaymentFields = () => {
    const localErrors = {};

    if (formData.ppd_tag === true) {
      const ppdRate = String(formData.ppd_rate ?? "").trim();
      const ppdTerm = String(formData.ppd_term ?? "").trim();

      if (!ppdRate) {
        localErrors.ppd_rate = [
          "PPD Rate is required when Prompt Payment is Yes.",
        ];
      } else {
        const parsedPpdRate = Number(ppdRate);

        if (!Number.isFinite(parsedPpdRate)) {
          localErrors.ppd_rate = ["PPD Rate must be a valid number."];
        } else if (parsedPpdRate < 0.5 || parsedPpdRate > 20) {
          localErrors.ppd_rate = ["PPD Rate must be between 0.5 and 20."];
        }
      }

      if (!ppdTerm || ppdTerm === "0") {
        localErrors.ppd_term = [
          "PPD Term is required when Prompt Payment is Yes.",
        ];
      }
    }

    return localErrors;
  };

  const handleMefUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataToSend = new FormData();
    formDataToSend.append("file", file);

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${BASE_URL}/providers/mef/upload`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      setFormData({
        ...formData,
        mef_upload_id: response.data.upload_id,
        mef_file_name: response.data.original_file_name,
        mef_signed_url: response.data.signed_url,
        mef_expires_at: response.data.expires_at,
      });
      setIsFormDirty(true);
      toast.success("MEF file uploaded successfully!", { autoClose: 3000 });
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to upload MEF file.",
        {
          autoClose: 3000,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateMefUrl = async () => {
    if (!formData.mef_upload_id) {
      toast.error("No MEF file uploaded.", { autoClose: 3000 });
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${BASE_URL}/providers/mef/regenerate-url`,
        { upload_id: formData.mef_upload_id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      setFormData({
        ...formData,
        mef_signed_url: response.data.signed_url,
        mef_expires_at: response.data.expires_at,
      });
      toast.success("MEF URL regenerated successfully!", { autoClose: 3000 });
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to regenerate MEF URL.",
        {
          autoClose: 3000,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleBankProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataToSend = new FormData();
    formDataToSend.append("file", file);
    formDataToSend.append("entity_type", "provider");
    if (providerCode) {
      formDataToSend.append("entity_id", providerCode);
    }

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${BASE_URL}/providers/bank-proof/upload`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      const data = response.data.data || {};
      setFormData({
        ...formData,
        bank_proof_upload_id: data.upload_id || "",
        bank_proof_file_name: data.original_file_name || "",
        bank_proof_signed_url: data.signed_url || "",
        bank_proof_expires_at: data.expires_at || "",
      });
      setIsFormDirty(true);
      toast.success("Bank proof uploaded successfully!", { autoClose: 3000 });
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to upload bank proof.",
        {
          autoClose: 3000,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateBankProofUrl = async () => {
    if (!formData.bank_proof_upload_id) {
      toast.error("No bank proof file uploaded.", { autoClose: 3000 });
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${BASE_URL}/providers/bank-proof/regenerate-url`,
        { upload_id: formData.bank_proof_upload_id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );

      setFormData({
        ...formData,
        bank_proof_signed_url: response.data.signed_url,
        bank_proof_expires_at: response.data.expires_at,
      });
      toast.success("Bank proof URL regenerated successfully!", {
        autoClose: 3000,
      });
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to regenerate bank proof URL.",
        {
          autoClose: 3000,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataToSend = new FormData();
    formDataToSend.append("file", file);

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${BASE_URL}/providers/document/upload`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      setFormData({
        ...formData,
        document_upload_id: response.data.upload_id,
        document_file_name: response.data.original_file_name,
        document_signed_url: response.data.signed_url,
        document_expires_at: response.data.expires_at,
      });
      setIsFormDirty(true);
      toast.success("Sign-off document uploaded successfully!", {
        autoClose: 3000,
      });
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to upload sign-off document.",
        {
          autoClose: 3000,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateDocumentUrl = async () => {
    if (!formData.document_upload_id) {
      toast.error("No sign-off document uploaded.", { autoClose: 3000 });
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${BASE_URL}/providers/document/regenerate-url`,
        { upload_id: formData.document_upload_id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      setFormData({
        ...formData,
        document_signed_url: response.data.signed_url,
        document_expires_at: response.data.expires_at,
      });
      toast.success("Document URL regenerated successfully!", {
        autoClose: 3000,
      });
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to regenerate document URL.",
        {
          autoClose: 3000,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isMefUrlExpired = () => {
    if (!formData.mef_expires_at) return false;
    return new Date(formData.mef_expires_at) < new Date();
  };

  const isDocumentUrlExpired = () => {
    if (!formData.document_expires_at) return false;
    return new Date(formData.document_expires_at) < new Date();
  };

  const isBankProofUrlExpired = () => {
    if (!formData.bank_proof_expires_at) return false;
    return new Date(formData.bank_proof_expires_at) < new Date();
  };

  const LEGACY_UPLOAD_BASE_URL = "https://allaccess.net.ph/upload_doc";
  const LEGACY_UPLOAD_PATH_REGEX = /\/upload_doc\//i;

  const isLegacyUploadUrl = (value) =>
    LEGACY_UPLOAD_PATH_REGEX.test(String(value || ""));

  const looksLikeLegacyFileName = (value) =>
    /\.[a-z0-9]{2,8}(\?|#|$)/i.test(String(value || "").trim());

  const buildLegacyUploadUrl = (folder, rawValue) => {
    const value = String(rawValue || "").trim();
    if (!value) {
      return null;
    }

    if (/^https?:\/\//i.test(value)) {
      return isLegacyUploadUrl(value) ? value : null;
    }

    if (!looksLikeLegacyFileName(value)) {
      return null;
    }

    const normalizedFolder = String(folder || "")
      .trim()
      .replace(/^\/+|\/+$/g, "");
    if (!normalizedFolder) {
      return null;
    }

    let normalizedFileName = value;
    try {
      normalizedFileName = decodeURIComponent(value);
    } catch (error) {
      normalizedFileName = value;
    }

    return `${LEGACY_UPLOAD_BASE_URL}/${encodeURIComponent(normalizedFolder)}/${encodeURIComponent(normalizedFileName)}`;
  };

  const resolveLegacyUploadUrl = ({ folder, fallbackUrl = "", candidates = [] }) => {
    const fallback = String(fallbackUrl || "").trim();
    if (isLegacyUploadUrl(fallback)) {
      return fallback;
    }

    for (const candidate of candidates) {
      const legacyUrl = buildLegacyUploadUrl(folder, candidate);
      if (legacyUrl) {
        return legacyUrl;
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnlyMode) return;
    setErrors({});
    setPendingErrorFocusKey("");

    if (activeTab === "maintenance") {
      await handleMaintenanceSubmit();
      return;
    }

    if (providerBankChanged() && !formData.bank_proof_upload_id) {
      setActiveTab("payment_terms");
      setPendingErrorFocusKey("bank_proof_upload_id");
      toast.error("Bank proof is required when provider bank details are changed.", {
        autoClose: 3000,
      });
      return;
    }

    const promptPaymentErrors = validatePromptPaymentFields();
    if (Object.keys(promptPaymentErrors).length > 0) {
      const firstErrorKey = Object.keys(promptPaymentErrors)[0] || "ppd_rate";
      setActiveTab("payment_terms");
      setErrors(promptPaymentErrors);
      setPendingErrorFocusKey(firstErrorKey);
      toast.error(
        promptPaymentErrors[firstErrorKey]?.[0] ||
          "Please complete Prompt Payment details.",
        { autoClose: 4000 }
      );
      return;
    }

    if (
      formData.accreditation_date &&
      formData.disaccreditation_date &&
      new Date(formData.disaccreditation_date) <= new Date(formData.accreditation_date)
    ) {
      setActiveTab("miscellaneous");
      setErrors({
        accreditation_date: ["Accreditation Date must be before Disaccreditation Date."],
        disaccreditation_date: ["Disaccreditation Date must be after Accreditation Date."],
      });
      setPendingErrorFocusKey("disaccreditation_date");
      toast.error("Disaccreditation Date must be after Accreditation Date.", {
        autoClose: 3000,
      });
      return;
    }

    const { localErrors: contactDetailErrors, normalizedContactDetails } =
      validateAndNormalizeContactDetails(formData.contact_details);
    if (Object.keys(contactDetailErrors).length > 0) {
      const firstLocalErrorKey = Object.keys(contactDetailErrors)[0] || "";
      setActiveTab("contact_details");
      setErrors(contactDetailErrors);
      setPendingErrorFocusKey(firstLocalErrorKey);
      toast.error(
        firstLocalErrorKey
          ? contactDetailErrors[firstLocalErrorKey][0]
          : "Please fix Contact Details validation errors.",
        {
          autoClose: 4000,
        }
      );
      return;
    }

    const formDataToSend = {
      ...formData,
      contact_details: normalizedContactDetails,
      physician_affiliations: formData.physician_affiliations.map(
        ({ physician_id, room_no, schedule, remarks }) => ({
          physician_id,
          room_no,
          schedule,
          remarks,
        })
      ),
      cpt_rates: formData.cpt_rates.map((rate) => ({
        cpt_code: rate.cpt_code || rate.master_cpt_code || "",
        cpt_rate: rate.cpt_rate ?? null,
        provider_cpt_code: rate.provider_cpt_code || "",
        provider_cpt_desc:
          rate.provider_cpt_desc || rate.cpt_desc || rate.cpt_description || "",
        cpt_rate_major: rate.cpt_rate_major ?? null,
        cpt_pkg_code: rate.cpt_pkg_code ?? null,
      })),
    };
    delete formDataToSend.mef_file_name;
    delete formDataToSend.mef_signed_url;
    delete formDataToSend.mef_expires_at;
    delete formDataToSend.bank_proof_file_name;
    delete formDataToSend.bank_proof_signed_url;
    delete formDataToSend.bank_proof_expires_at;
    delete formDataToSend.document_file_name;
    delete formDataToSend.document_signed_url;
    delete formDataToSend.document_expires_at;

    try {
      setIsLoading(true);
      const url = isEditMode
        ? `${BASE_URL}/providers/${providerCode}`
        : `${BASE_URL}/providers`;
      const method = isEditMode ? "put" : "post";
      const response = await axios({
        method,
        url,
        data: formDataToSend,
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      });
      toast.success(response.data.message, {
        autoClose: 3000,
        onClose: () => {
          // alert(
          //   isEditMode
          //     ? "Provider updated successfully."
          //     : "Provider added successfully."
          // );
          // navigate("/providers");
        },
      });
    } catch (error) {
      if (error.response?.status === 422) {
        const backendErrors = error.response.data.errors || {};
        const {
          normalizedErrors,
          formattedMessages,
          firstErrorKey,
        } = normalizeBackendErrors(backendErrors);

        setErrors(normalizedErrors);

        if (firstErrorKey) {
          preparePaginationForErrorKey(firstErrorKey);
          setActiveTab(resolveTabFromErrorKey(firstErrorKey));
          setPendingErrorFocusKey(firstErrorKey);
        }

        toast.error(
          formattedMessages.join(" ") || "Please fix the highlighted fields.",
          { autoClose: 5000 }
        );
      } else if (error.response?.status === 409) {
        toast.error(error.response.data.error, { autoClose: 3000 });
      } else {
        toast.error(
          isEditMode
            ? "An error occurred while updating the provider."
            : "An error occurred while adding the provider.",
          { autoClose: 3000 }
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const addPhysicianAffiliation = () => {
    if (!newPhysicianAffiliation.physician_id) {
      toast.error("Please select a physician first.", { autoClose: 3000 });
      return;
    }

    const normalizedSelectedPhysicianId = String(newPhysicianAffiliation.physician_id)
      .trim()
      .toUpperCase();

    if (
      formData.physician_affiliations.some(
        (affiliation) =>
          String(affiliation.physician_id || "")
            .trim()
            .toUpperCase() === normalizedSelectedPhysicianId
      )
    ) {
      toast.error("Selected physician is already in the affiliation table.", {
        autoClose: 3000,
      });
      return;
    }

    const selectedPhysician = physicians.find(
      (physician) =>
        physician.physician_id === newPhysicianAffiliation.physician_id
    );

    const affiliationPayload = {
      ...newPhysicianAffiliation,
      full_name: selectedPhysician?.full_name || "",
      specialization: selectedPhysician?.specialization || "",
      sub_specialization: selectedPhysician?.sub_specialization || "",
    };

    setFormData((prev) => ({
      ...prev,
      physician_affiliations: [
        ...prev.physician_affiliations,
        affiliationPayload,
      ],
    }));
    setNewPhysicianAffiliation({
      physician_id: "",
      full_name: "",
      specialization: "",
      sub_specialization: "",
      room_no: "",
      schedule: "",
      remarks: "",
    });
    setIsFormDirty(true);
  };

  const removePhysicianAffiliation = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      physician_affiliations: prev.physician_affiliations.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
    setIsFormDirty(true);
  };

  const addCptRate = () => {
    if (!newCptRate.cpt_code) {
      toast.error("Please select a CPT code first.", { autoClose: 3000 });
      return;
    }

    const normalizedSelectedCptCode = String(newCptRate.cpt_code)
      .trim()
      .toUpperCase();

    if (
      formData.cpt_rates.some(
        (rate) =>
          String(rate.cpt_code || "")
            .trim()
            .toUpperCase() === normalizedSelectedCptCode
      )
    ) {
      toast.error("Selected CPT code is already in the CPT table.", {
        autoClose: 3000,
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      cpt_rates: [...prev.cpt_rates, { ...newCptRate }],
    }));
    setNewCptRate({
      cpt_code: "",
      cpt_description: "",
      cpt_rate: "",
      provider_cpt_code: "",
    });
    setIsFormDirty(true);
  };

  const removeCptRate = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      cpt_rates: prev.cpt_rates.filter((_, index) => index !== indexToRemove),
    }));
    setIsFormDirty(true);
  };

  const getApiErrorMessage = (error, fallbackMessage) => {
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      fallbackMessage;

    if (typeof message === "string") {
      return message;
    }

    if (message && typeof message === "object") {
      return Object.values(message)
        .flat()
        .filter(Boolean)
        .join(" ");
    }

    return fallbackMessage;
  };

  const openProviderCptAddModal = () => {
    setProviderCptForm({
      master_cpt_code: "",
      master_cpt_desc: "",
      provider_cpt_code: "",
      cpt_desc: "",
      cpt_rate: "",
      cpt_source: "",
    });
    setProviderCptModalMode("add");
  };

  const openProviderCptEditModal = (row) => {
    setProviderCptForm({
      master_cpt_code: row.master_cpt_code || row.cpt_code || "",
      master_cpt_desc: row.master_cpt_desc || row.cpt_description || "",
      provider_cpt_code: row.provider_cpt_code || row.cpt_code || "",
      cpt_desc: row.cpt_desc || row.provider_cpt_desc || row.cpt_description || "",
      cpt_rate: row.cpt_rate || "",
      cpt_source: row.cpt_source || "",
    });
    setProviderCptModalMode("edit");
  };

  const closeProviderCptModal = () => {
    if (isProviderCptSaving) return;
    setProviderCptModalMode("");
  };

  const handleProviderCptFormChange = (e) => {
    const { name, value } = e.target;
    setProviderCptForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveProviderCpt = async (duplicateAction = "") => {
    if (!providerCode) {
      toast.error("Provider code is required before saving CPT.", {
        autoClose: 3000,
      });
      return;
    }

    if (!providerCptForm.cpt_desc.trim()) {
      toast.error("CPT description is required.", { autoClose: 3000 });
      return;
    }

    if (
      String(providerCptForm.cpt_rate || "").trim() === "" ||
      Number.isNaN(Number(providerCptForm.cpt_rate))
    ) {
      toast.error("Amount must be numeric.", { autoClose: 3000 });
      return;
    }

    try {
      setIsProviderCptSaving(true);
      const response = await axios.post(
        `${BASE_URL}/providers/${providerCode}/cpt`,
        {
          master_cpt_code: providerCptForm.master_cpt_code,
          provider_cpt_code: providerCptForm.provider_cpt_code,
          cpt_desc: providerCptForm.cpt_desc,
          cpt_rate: providerCptForm.cpt_rate,
          mode: providerCptModalMode || "edit",
          duplicate_action: duplicateAction,
        },
        {
          headers: getAuthHeaders(),
        }
      );

      toast.success(response.data?.message || "Provider CPT saved.", {
        autoClose: 3000,
      });
      setProviderCptModalMode("");
      await loadProviderCptList();
      setIsFormDirty(false);
    } catch (error) {
      if (
        error.response?.status === 409 &&
        error.response?.data?.conflict_type === "provider_cpt_code" &&
        duplicateAction !== "update_existing"
      ) {
        setIsProviderCptSaving(false);
        const confirmed = window.confirm(
          "Provider CPT code already exists for this provider. Do you want to update the existing CPT row instead?"
        );
        if (confirmed) {
          await handleSaveProviderCpt("update_existing");
        }
        return;
      }

      toast.error(
        getApiErrorMessage(error, "Unable to save provider CPT."),
        { autoClose: 5000 }
      );
    } finally {
      setIsProviderCptSaving(false);
    }
  };

  const clearProviderCptFile = () => {
    setProviderCptFile(null);
    if (providerCptFileInputRef.current) {
      providerCptFileInputRef.current.value = "";
    }
  };

  const handleProviderCptFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setProviderCptFile(file);
    setProviderCptPreview(null);
  };

  const handleProviderCptTemplateDownload = async () => {
    if (!providerCode) {
      toast.error("Provider code is required before downloading template.", {
        autoClose: 3000,
      });
      return;
    }

    try {
      const response = await axios.get(
        `${BASE_URL}/providers/${providerCode}/cpt-template`,
        {
          responseType: "blob",
          headers: getAuthHeaders(),
        }
      );
      const filename = getDownloadFilename(
        response.headers["content-disposition"],
        `${providerCode}_provider_cpt_template.csv`
      );
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "text/csv;charset=utf-8",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Unable to download CPT template."),
        { autoClose: 5000 }
      );
    }
  };

  const handleProviderCptPreviewBatch = async () => {
    if (!providerCode) {
      toast.error("Provider code is required before batch preview.", {
        autoClose: 3000,
      });
      return;
    }

    if (!providerCptFile) {
      toast.error("Please browse a CSV file first.", { autoClose: 3000 });
      return;
    }

    const payload = new FormData();
    payload.append("file", providerCptFile);

    try {
      setIsProviderCptPreviewing(true);
      const response = await axios.post(
        `${BASE_URL}/providers/${providerCode}/cpt-batch/preview`,
        payload,
        {
          headers: getAuthHeaders(),
        }
      );
      setProviderCptPreview(response.data);
      toast.success("Batch preview is ready.", { autoClose: 3000 });
    } catch (error) {
      setProviderCptPreview(null);
      toast.error(
        getApiErrorMessage(error, "Unable to preview CPT batch."),
        { autoClose: 5000 }
      );
    } finally {
      setIsProviderCptPreviewing(false);
    }
  };

  const handleProviderCptCommitBatch = async () => {
    if (!providerCptPreview?.preview_id) {
      toast.error("Preview the batch before committing changes.", {
        autoClose: 3000,
      });
      return;
    }

    try {
      setIsProviderCptCommitting(true);
      const response = await axios.post(
        `${BASE_URL}/providers/${providerCode}/cpt-batch/commit`,
        { preview_id: providerCptPreview.preview_id },
        {
          headers: getAuthHeaders(),
        }
      );
      setProviderCptPreview(response.data);
      clearProviderCptFile();
      await loadProviderCptList();
      toast.success("Batch CPT changes committed.", { autoClose: 3000 });
      setIsFormDirty(false);
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Unable to commit CPT batch."),
        { autoClose: 5000 }
      );
    } finally {
      setIsProviderCptCommitting(false);
    }
  };

  const physicianOptions = physicians
    .filter(
      (physician) =>
        !formData.physician_affiliations.some(
          (affiliation) =>
            String(affiliation.physician_id || "")
              .trim()
              .toUpperCase() ===
            String(physician.physician_id || "")
              .trim()
              .toUpperCase()
        )
    )
    .map((physician) => ({
      value: physician.physician_id,
      label: physician.full_name
        ? String(physician.full_name).trim()
        : "Unknown Physician",
      specialization: physician.specialization || "",
      sub_specialization: physician.sub_specialization || "",
    }));

  const selectedPhysicianOption =
    physicianOptions.find(
      (option) => option.value === newPhysicianAffiliation.physician_id
    ) ||
    (newPhysicianAffiliation.physician_id
      ? {
          value: newPhysicianAffiliation.physician_id,
          label: newPhysicianAffiliation.full_name
            ? String(newPhysicianAffiliation.full_name).trim()
            : String(newPhysicianAffiliation.physician_id).trim(),
          specialization: newPhysicianAffiliation.specialization || "",
          sub_specialization: newPhysicianAffiliation.sub_specialization || "",
        }
      : null);

  const cptSelectOptions = cptOptions
    .filter(
      (cpt) =>
        !formData.cpt_rates.some(
          (rate) =>
            String(rate.cpt_code || "")
              .trim()
              .toUpperCase() ===
            String(cpt.cpt_code || "")
              .trim()
              .toUpperCase()
        )
    )
    .map((cpt) => ({
      value: cpt.cpt_code,
      label: `${cpt.cpt_code} - ${cpt.cpt_desc || "No description"}`,
      description: cpt.cpt_desc || "",
    }));

  const selectedCptOption =
    cptSelectOptions.find((option) => option.value === newCptRate.cpt_code) ||
    (newCptRate.cpt_code
      ? {
          value: newCptRate.cpt_code,
          label: `${newCptRate.cpt_code} - ${newCptRate.cpt_description || "No description"}`,
          description: newCptRate.cpt_description || "",
        }
      : null);

  const providerOptions = providers.map((provider) => ({
    value: provider.provider_code,
    label: `${provider.provider_code} - ${provider.provider_name || "No name"}`,
    tin: provider.tin || "",
  }));

  const physicianAffiliationRows = formData.physician_affiliations.map(
    (affiliation, index) => {
      const selectedPhysician = physicians.find(
        (physician) => physician.physician_id === affiliation.physician_id
      );
      return {
        ...affiliation,
        rowIndex: index,
        physician_name:
          affiliation.physician_name ||
          affiliation.full_name ||
          selectedPhysician?.full_name ||
          "",
        specialization:
          affiliation.specialization || selectedPhysician?.specialization || "",
        sub_specialization:
          affiliation.sub_specialization ||
          selectedPhysician?.sub_specialization ||
          "",
      };
    }
  );

  const physicianAffiliationFilteredRows = physicianAffiliationRows.filter(
    (affiliation) => {
      const query = physicianAffiliationSearchQuery.toLowerCase();
      return (
        String(affiliation.physician_id || "")
          .toLowerCase()
          .includes(query) ||
        String(affiliation.physician_name || "")
          .toLowerCase()
          .includes(query) ||
        String(affiliation.specialization || "")
          .toLowerCase()
          .includes(query) ||
        String(affiliation.sub_specialization || "")
          .toLowerCase()
          .includes(query) ||
        String(affiliation.room_no || "")
          .toLowerCase()
          .includes(query) ||
        String(affiliation.schedule || "")
          .toLowerCase()
          .includes(query) ||
        String(affiliation.remarks || "")
          .toLowerCase()
          .includes(query)
      );
    }
  );

  const physicianAffiliationTotalItems = physicianAffiliationFilteredRows.length;
  const physicianAffiliationTotalPages = Math.max(
    1,
    Math.ceil(
      physicianAffiliationTotalItems / physicianAffiliationItemsPerPage || 1
    )
  );
  const physicianAffiliationStartIndex =
    (physicianAffiliationCurrentPage - 1) * physicianAffiliationItemsPerPage;
  const physicianAffiliationPaginatedRows = physicianAffiliationFilteredRows.slice(
    physicianAffiliationStartIndex,
    physicianAffiliationStartIndex + physicianAffiliationItemsPerPage
  );

  const handlePhysicianAffiliationItemsPerPageChange = (e) => {
    setPhysicianAffiliationItemsPerPage(Number(e.target.value));
    setPhysicianAffiliationCurrentPage(1);
  };

  const handlePhysicianAffiliationPageChange = (page) => {
    if (page >= 1 && page <= physicianAffiliationTotalPages) {
      setPhysicianAffiliationCurrentPage(page);
    }
  };

  const cptRateRows = (useProviderCptApi ? providerCptRows : formData.cpt_rates).map((rate, index) => ({
    ...rate,
    rowIndex: index,
  }));

  const filteredCptRateRows = cptRateRows.filter((rate) => {
    const query = cptSearchQuery.toLowerCase();
    return (
      String(rate.master_cpt_code || "")
        .toLowerCase()
        .includes(query) ||
      String(rate.master_cpt_desc || "")
        .toLowerCase()
        .includes(query) ||
      String(rate.cpt_code || "")
        .toLowerCase()
        .includes(query) ||
      String(rate.cpt_description || "")
        .toLowerCase()
        .includes(query) ||
      String(rate.cpt_desc || rate.provider_cpt_desc || "")
        .toLowerCase()
        .includes(query) ||
      String(rate.cpt_rate || "")
        .toLowerCase()
        .includes(query) ||
      String(rate.provider_cpt_code || "")
        .toLowerCase()
        .includes(query)
    );
  });

  const totalCptItems = filteredCptRateRows.length;
  const totalCptPages = Math.max(
    1,
    Math.ceil(totalCptItems / cptItemsPerPage || 1)
  );
  const cptStartIndex = (cptCurrentPage - 1) * cptItemsPerPage;
  const paginatedCptRateRows = filteredCptRateRows.slice(
    cptStartIndex,
    cptStartIndex + cptItemsPerPage
  );

  const handleCptItemsPerPageChange = (e) => {
    setCptItemsPerPage(Number(e.target.value));
    setCptCurrentPage(1);
  };

  const handleCptPageChange = (page) => {
    if (page >= 1 && page <= totalCptPages) {
      setCptCurrentPage(page);
    }
  };

  const getDownloadFilename = (contentDisposition, fallbackFilename) => {
    if (!contentDisposition) return fallbackFilename;

    const utf8FileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8FileNameMatch?.[1]) {
      try {
        return decodeURIComponent(utf8FileNameMatch[1]);
      } catch (error) {
        return utf8FileNameMatch[1];
      }
    }

    const fileNameMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
    if (fileNameMatch?.[1]) {
      return fileNameMatch[1];
    }

    return fallbackFilename;
  };

  const handleExportProviderTabData = async (
    section,
    format,
    searchValue = ""
  ) => {
    if (!providerCode) {
      toast.error("Provider code is required before exporting.", {
        autoClose: 3000,
      });
      return;
    }

    const isPhysicianSection = section === "physician-affiliations";
    if (isPhysicianSection) {
      setIsExportingPhysician(true);
    } else {
      setIsExportingCpt(true);
    }

    try {
      const response = await axios.get(
        `${BASE_URL}/providers/${providerCode}/export/${section}/${format}`,
        {
          params: searchValue ? { search: searchValue } : {},
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );

      const fallbackFilename = `${providerCode}_${section.replace(
        /-/g,
        "_"
      )}.${format}`;
      const filename = getDownloadFilename(
        response.headers["content-disposition"],
        fallbackFilename
      );

      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          (format === "pdf" ? "application/pdf" : "text/csv;charset=utf-8"),
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        `Failed to export ${isPhysicianSection ? "physician affiliations" : "CPT rates"} as ${format.toUpperCase()}.`,
        { autoClose: 3000 }
      );
    } finally {
      if (isPhysicianSection) {
        setIsExportingPhysician(false);
      } else {
        setIsExportingCpt(false);
      }
    }
  };

  const ConfirmationPrompt = ({ onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Unsaved Changes
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Are you sure you want to leave? Any unsaved changes will be lost.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors text-xs"
          >
            {isEditMode ? "Continue Editing" : "Continue Adding"}
          </button>
          <button
            onClick={onConfirm}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors text-xs"
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  );

  const MaintenanceConfirmationPrompt = ({ onConfirm, onCancel, payload }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Confirm Maintenance Update
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Proceed with this provider maintenance movement?
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-gray-700 space-y-1">
          <p>
            <span className="font-semibold">Update For:</span>{" "}
            {payload?.update_for ? toTitleCaseFromSnake(payload.update_for) : "N/A"}
          </p>
          <p>
            <span className="font-semibold">Effectivity Date:</span>{" "}
            {payload?.effectivity_date || "N/A"}
          </p>
          <p>
            <span className="font-semibold">Reason:</span>{" "}
            {payload?.reason || "N/A"}
          </p>
          <p>
            <span className="font-semibold">Remarks:</span>{" "}
            {payload?.remarks || "N/A"}
          </p>
        </div>
        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors text-xs"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="bg-emerald-600 text-white px-4 py-2 rounded-md hover:bg-emerald-700 transition-colors text-xs"
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  );

  // Update useEffect for beforeunload to ensure browser navigation prompt
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isFormDirty) {
        e.preventDefault();
        e.returnValue =
          "Are you sure you want to leave? Any unsaved changes will be lost.";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isFormDirty]);

  // Add state for showing confirmation prompt
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);

  const getContactDepartmentLabel = (departmentValue, index) => {
    const rawValue = String(departmentValue ?? "").trim();
    if (/^\d+$/.test(rawValue)) {
      const mappedLabel = CONTACT_DEPARTMENT_LABELS[Number(rawValue)];
      if (mappedLabel) return mappedLabel;
    }

    if (rawValue) return rawValue;
    return CONTACT_DEPARTMENT_LABELS[index] || `Department ${index + 1}`;
  };

  const handlePrintProvider = () => {
    const opened = openProviderPrintPreview({
      formData,
      providerCode,
      getContactDepartmentLabel,
    });

    if (!opened) {
      toast.error("Popup blocked. Please allow popups to open print preview.");
    }
  };

  // Add navigation blocking logic
  const blocker = useCallback(
    ({ nextLocation }) => {
      if (isFormDirty && !showConfirmation) {
        setNextLocation(nextLocation);
        setShowConfirmation(true);
        return false;
      }
      return true;
    },
    [isFormDirty, showConfirmation]
  );

  // Add useEffect to reset isFormDirty after successful submission
  useEffect(() => {
    if (!isLoading) {
      setIsFormDirty(false);
    }
  }, [isLoading]);

  // Add useEffect to reset initialFormData when formData changes in edit mode
  useEffect(() => {
    if (isEditMode && !isLoading) {
      initialFormData.current = formData;
      setIsFormDirty(false);
    }
  }, [isEditMode, isLoading, formData]);

  useEffect(() => {
    if (physicianAffiliationCurrentPage > physicianAffiliationTotalPages) {
      setPhysicianAffiliationCurrentPage(physicianAffiliationTotalPages);
    }
  }, [physicianAffiliationCurrentPage, physicianAffiliationTotalPages]);

  useEffect(() => {
    if (cptCurrentPage > totalCptPages) {
      setCptCurrentPage(totalCptPages);
    }
  }, [cptCurrentPage, totalCptPages]);

  const providerDocumentEntries = [
    {
      key: "signoff",
      label: "Sign-off Document",
      fileName:
        formData.document_file_name ||
        formData.document_path ||
        formData.document_upload_id,
      signedUrl: formData.document_signed_url,
      legacyUrl: resolveLegacyUploadUrl({
        folder: "provider_2303",
        fallbackUrl: formData.document_path,
        candidates: [formData.document_path, formData.document_upload_id],
      }),
      expiresAt: formData.document_expires_at,
      isExpired: isDocumentUrlExpired(),
      canRegenerate: !!formData.document_upload_id,
      onRegenerate: handleRegenerateDocumentUrl,
    },
    {
      key: "mef",
      label: "Bank Details Upload (MEF)",
      fileName: formData.mef_file_name || formData.mef_upload_id || formData.mef,
      signedUrl: formData.mef_signed_url,
      legacyUrl: resolveLegacyUploadUrl({
        folder: "mef",
        candidates: [formData.mef_upload_id, formData.mef],
      }),
      expiresAt: formData.mef_expires_at,
      isExpired: isMefUrlExpired(),
      canRegenerate: !!formData.mef_upload_id,
      onRegenerate: handleRegenerateMefUrl,
    },
    {
      key: "bank_proof",
      label: "Bank Proof",
      fileName:
        formData.bank_proof_file_name ||
        formData.bank_statement_file ||
        formData.bank_proof_upload_id,
      signedUrl: formData.bank_proof_signed_url,
      legacyUrl: resolveLegacyUploadUrl({
        folder: "bank_statement",
        candidates: [
          formData.bank_statement_file,
          formData.bank_statement,
          formData.bank_proof_upload_id,
        ],
      }),
      expiresAt: formData.bank_proof_expires_at,
      isExpired: isBankProofUrlExpired(),
      canRegenerate: !!formData.bank_proof_upload_id,
      onRegenerate: handleRegenerateBankProofUrl,
    },
  ];
  const hasProviderDocuments = providerDocumentEntries.some(
    (documentEntry) =>
      !!(documentEntry.fileName || documentEntry.signedUrl || documentEntry.legacyUrl)
  );

  return (
    <div className="min-h-screen bg-gray-50 py-2 px-2 sm:px-3 lg:px-4">
      <ToastContainer position="top-right" autoClose={3000} />
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-1xl mx-auto"
      >
        {isLoading && isEditMode && (
          <div className="flex justify-center items-center mb-4">
            <div className="flex items-center space-x-2 text-gray-600">
              <svg
                className="animate-spin h-5 w-5 text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="text-sm">
                Please wait while loading your data...
              </span>
            </div>
          </div>
        )}
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-xl font-semibold text-gray-900">
            {isReadOnlyMode ? "View Provider: " + formData.provider_name : isEditMode ? "Edit Provider: " + formData.provider_name : "Add New Provider"}
          </h1>

          {/* <a
            href="/providers"
            onClick={(e) => {
              if (isFormDirty) {
                e.preventDefault();
                setNextLocation("/providers");
                setShowConfirmation(true);
              }
            }}
            className="flex items-center space-x-1 bg-gray-600 text-white px-3 py-1.5 rounded-md hover:bg-gray-700 transition-colors text-xs"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Back to Providers</span>
          </a> */}
          <div className="flex items-center space-x-2">
            {isReadOnlyMode && (
              <button
                type="button"
                onClick={() => navigate(`/medical/update-provider/${providerCode}`)}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
              >
                <PencilSquareIcon className="h-4 w-4" />
                <span>Edit Provider</span>
              </button>
            )}
            {isReadOnlyMode && (
              <button
                type="button"
                onClick={handlePrintProvider}
                className="flex items-center space-x-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium shadow-sm"
              >
                <PrinterIcon className="h-4 w-4" />
                <span>Print</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowDocumentsModal(true)}
              className="flex items-center space-x-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-50"
              disabled={!hasProviderDocuments || isLoading}
              title={
                hasProviderDocuments
                  ? "Open provider documents"
                  : "No uploaded documents available"
              }
            >
              <FolderOpenIcon className="h-4 w-4" />
              <span>Download Document</span>
            </button>
            <button
              // onClick={() => navigate(-1)}
               onClick={(e) => {
                if (isFormDirty) {
                  e.preventDefault();
                  setNextLocation("/providers");
                  setShowConfirmation(true);
                } else {
                  navigate(-1)
                }
              }}
              className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Go Back</span>
            </button>
          </div>
        </div>

        <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-4">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              {[
                "information",
                "contact_details",
                "payment_terms",
                "signatories",
                "internet_connectivity",
                "miscellaneous",
                "physician_affiliation",
                ...(isEditMode ? ["maintenance"] : []),
                "cpt",
              ].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`${
                    activeTab === tab
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                >
                  {tab
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
                </button>
              ))}
            </nav>
          </div>

          <form onSubmit={handleSubmit} className="mt-4">
            {activeTab === "information" && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Provider Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="provider_name"
                      value={formData.provider_name}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        errors.provider_name
                          ? "border-red-500"
                          : "border-gray-200"
                      } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                      placeholder="Enter name"
                      disabled={isFormLocked}
                    />
                    {errors.provider_name && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.provider_name[0]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Short Name
                    </label>
                    <input
                      type="text"
                      name="short_name"
                      value={formData.short_name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter short name"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Licensed Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="licensed_name"
                      value={formData.licensed_name}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        errors.licensed_name
                          ? "border-red-500"
                          : "border-gray-200"
                      } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                      placeholder="Enter licensed name"
                      disabled={isFormLocked}
                    />
                    {errors.licensed_name && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.licensed_name[0]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Provider Type
                    </label>
                    <div className="mt-1 flex space-x-4">
                      <label className="flex items-center text-xs">
                        <input
                          type="radio"
                          name="provider_type"
                          value="h"
                          checked={formData.provider_type === "h"}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        Hospital
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="radio"
                          name="provider_type"
                          value="c"
                          checked={formData.provider_type === "c"}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        Clinic
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      PHIC Accreditation
                    </label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <input
                        type="date"
                        name="phic_datefrom"
                        value={formData.phic_datefrom}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        placeholder="(From)"
                        disabled={isFormLocked}
                      />
                      <input
                        type="date"
                        name="phic_dateto"
                        value={formData.phic_dateto}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        placeholder="(To)"
                        disabled={isFormLocked}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Accreditation Status
                    </label>
                    <select
                      name="accreditation_flag"
                      value={formData.accreditation_flag}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      disabled={isFormLocked}
                    >
                      <option value="0">Accredited</option>
                      <option value="1">Disaccredited</option>
                      <option value="2">Non-accredited</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Remarks
                    </label>
                    <textarea
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      rows={3}
                      disabled={isFormLocked}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        name="fraud_flag"
                        checked={formData.fraud_flag}
                        onChange={handleInputChange}
                        className="mr-1"
                        disabled={isFormLocked}
                      />
                      Suspected Fraudulent Provider
                    </label>
                    <label className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        name="spec_transmittal"
                        checked={formData.spec_transmittal}
                        onChange={handleInputChange}
                        className="mr-1"
                        disabled={isFormLocked}
                      />
                      Special Case: With/without Transmittal
                    </label>
                    <label className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        name="default_corporate_no_access"
                        checked={formData.default_corporate_no_access}
                        onChange={handleInputChange}
                        className="mr-1"
                        disabled={isFormLocked}
                      />
                      Default Corporate No-access
                    </label>
                    <label className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        name="finalize_status"
                        checked={formData.finalize_status}
                        onChange={handleInputChange}
                        className="mr-1"
                        disabled={isFormLocked}
                      />
                      Finalized Provider
                    </label>
                  </div>
                </div>

                <h3 className="mt-4 text-sm font-semibold text-gray-900">
                  Business Address
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Street/Bldg/Subd/Vill
                    </label>
                    <input
                      type="text"
                      name="streetno_bldg"
                      value={formData.streetno_bldg}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter street/building/subd/village"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Region
                    </label>
                    {isLoadingAddress.region ? (
                      <div className="mt-1 flex items-center space-x-2">
                        <svg
                          className="animate-spin h-5 w-5 text-blue-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span className="text-xs text-gray-600">
                          Loading regions...
                        </span>
                      </div>
                    ) : (
                      <select
                        name="region"
                        value={formData.region}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        disabled={isFormLocked}
                      >
                        <option value="">Select a region</option>
                        {regions.map((region) => (
                          <option
                            key={region.region_code}
                            value={region.region_code}
                          >
                            {region.region}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Province
                    </label>
                    {isLoadingAddress.province ? (
                      <div className="mt-1 flex items-center space-x-2">
                        <svg
                          className="animate-spin h-5 w-5 text-blue-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span className="text-xs text-gray-600">
                          Loading provinces...
                        </span>
                      </div>
                    ) : (
                      <select
                        name="province"
                        value={formData.province}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        disabled={!formData.region || isFormLocked}
                      >
                        <option value="">Select a province</option>
                        {provinces.map((province) => (
                          <option
                            key={province.province_code}
                            value={province.province_code}
                          >
                            {province.province}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Town City <span className="text-red-500">*</span>
                    </label>
                    {isLoadingAddress.towncity ? (
                      <div className="mt-1 flex items-center space-x-2">
                        <svg
                          className="animate-spin h-5 w-5 text-blue-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span className="text-xs text-gray-600">
                          Loading town cities...
                        </span>
                      </div>
                    ) : (
                      <select
                        name="towncity"
                        value={formData.towncity}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full border ${
                          errors.towncity ? "border-red-500" : "border-gray-200"
                        } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                        disabled={!formData.province || isFormLocked}
                      >
                        <option value="">Select a town city</option>
                        {townCities.map((city) => (
                          <option key={city.city_code} value={city.city_code}>
                            {city.city}
                          </option>
                        ))}
                      </select>
                    )}
                    {errors.towncity && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.towncity[0]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Barangay
                    </label>
                    {isLoadingAddress.barangay ? (
                      <div className="mt-1 flex items-center space-x-2">
                        <svg
                          className="animate-spin h-5 w-5 text-blue-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span className="text-xs text-gray-600">
                          Loading barangays...
                        </span>
                      </div>
                    ) : (
                      <select
                        name="barangay"
                        value={formData.barangay}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        disabled={!formData.towncity || isFormLocked}
                      >
                        <option value="">Select a barangay</option>
                        {barangays.map((barangay) => (
                          <option
                            key={barangay.baranggay_code}
                            value={barangay.baranggay_code}
                          >
                            {barangay.baranggay}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      name="postalcode"
                      value={formData.postalcode}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs bg-gray-100 cursor-not-allowed"
                      placeholder="Auto-populated"
                      disabled
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "contact_details" && (
              <div>
                {formData.contact_details.map((contact, index) => (
                  <div
                    key={index}
                    className="mb-4 rounded-lg border border-slate-200 bg-slate-50/70 overflow-hidden"
                  >
                    <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 bg-white">
                      <h3 className="text-sm font-semibold text-slate-900">
                        {getContactDepartmentLabel(
                          contact.provider_department,
                          index
                        )}
                      </h3>
                      <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                        Department {index + 1}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700">
                          Contact Person
                        </label>
                        <input
                          type="text"
                          value={contact.contact_person}
                          onChange={(e) =>
                            handleInputChange(e, index, "contact_person")
                          }
                          className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                          placeholder="Enter contact person"
                          disabled={isFormLocked}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">
                          Designation
                        </label>
                        <input
                          type="text"
                          value={contact.designation}
                          onChange={(e) =>
                            handleInputChange(e, index, "designation")
                          }
                          className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                          placeholder="Enter designation"
                          disabled={isFormLocked}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">
                          Mobile Number
                        </label>
                        <input
                          type="text"
                          value={contact.mobile_no}
                          onChange={(e) =>
                            handleInputChange(e, index, "mobile_no")
                          }
                          data-error-key={`contact_details.${index}.mobile_no`}
                          className={`mt-1 block w-full border ${
                            errors[`contact_details.${index}.mobile_no`]
                              ? "border-red-500"
                              : "border-gray-200"
                          } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                          placeholder="0917 123 4567, +63 917 123 4567"
                          disabled={isFormLocked}
                        />
                        {errors[`contact_details.${index}.mobile_no`] && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors[`contact_details.${index}.mobile_no`][0]}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">
                          Email Address
                        </label>
                        <input
                          type="text"
                          value={contact.email_add}
                          onChange={(e) =>
                            handleInputChange(e, index, "email_add")
                          }
                          data-error-key={`contact_details.${index}.email_add`}
                          className={`mt-1 block w-full border ${
                            errors[`contact_details.${index}.email_add`]
                              ? "border-red-500"
                              : "border-gray-200"
                          } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                          placeholder="email1@domain.com, email2@domain.com"
                          disabled={isFormLocked}
                        />
                        {errors[`contact_details.${index}.email_add`] && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors[`contact_details.${index}.email_add`][0]}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">
                          Fax Number
                        </label>
                        <input
                          type="text"
                          value={contact.fax_no}
                          onChange={(e) =>
                            handleInputChange(e, index, "fax_no")
                          }
                          data-error-key={`contact_details.${index}.fax_no`}
                          className={`mt-1 block w-full border ${
                            errors[`contact_details.${index}.fax_no`]
                              ? "border-red-500"
                              : "border-gray-200"
                          } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                          placeholder="(02) 8123-4567, +63 32 123 4567"
                          disabled={isFormLocked}
                        />
                        {errors[`contact_details.${index}.fax_no`] && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors[`contact_details.${index}.fax_no`][0]}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">
                          Landline Number
                        </label>
                        <input
                          type="text"
                          value={contact.landline_no}
                          onChange={(e) =>
                            handleInputChange(e, index, "landline_no")
                          }
                          data-error-key={`contact_details.${index}.landline_no`}
                          className={`mt-1 block w-full border ${
                            errors[`contact_details.${index}.landline_no`]
                              ? "border-red-500"
                              : "border-gray-200"
                          } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                          placeholder="(02) 8123-4567, 032 123 4567"
                          disabled={isFormLocked}
                        />
                        {errors[`contact_details.${index}.landline_no`] && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors[`contact_details.${index}.landline_no`][0]}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "payment_terms" && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  TIN Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2 rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Choose TIN from
                    </label>
                    <Select
                      options={providerOptions}
                      value={providerOptions.find(
                        (option) => option.value === formData.tin_from_provider
                      )}
                      onChange={handleProviderSelect}
                      onInputChange={handleProviderSearch}
                      placeholder="Search provider..."
                      isClearable
                      className="mt-1 text-xs"
                      classNamePrefix="select"
                      isDisabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Provider's TIN <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="tin"
                      value={formData.tin}
                      onChange={handleTinChange}
                      className={`mt-1 block w-full border ${
                        errors.tin ? "border-red-500" : "border-gray-200"
                      } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                      placeholder="Enter provider's TIN"
                      disabled={isFormLocked}
                    />
                    {errors.tin && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.tin[0]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Tax Exempt
                    </label>
                    <div className="mt-1 flex space-x-4">
                      <label className="flex items-center text-xs">
                        <input
                          type="radio"
                          name="tax_exempt_tag"
                          value={true}
                          checked={formData.tax_exempt_tag === true}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        Yes
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="radio"
                          name="tax_exempt_tag"
                          value={false}
                          checked={formData.tax_exempt_tag === false}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        No
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      VAT Registered
                      <span className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold text-white bg-red-600">
                        New
                      </span>
                    </label>
                    <select
                      name="vat_registered"
                      value={formData.vat_registered}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      disabled={isFormLocked}
                    >
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        name="withheld"
                        checked={formData.withheld}
                        onChange={handleInputChange}
                        className="mr-1"
                        disabled={isFormLocked}
                      />
                      Withheld
                    </label>
                  </div>
                </div>

                <h3 className="mt-4 text-sm font-semibold text-gray-900">
                  Corporate Bank Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Account Name
                    </label>
                    <input
                      type="text"
                      name="bank_acct_name"
                      value={formData.bank_acct_name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter account name"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Account Number
                    </label>
                    <input
                      type="text"
                      name="bank_acct_no"
                      value={formData.bank_acct_no}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter account number"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Bank Name
                    </label>
                    {isLoading ? (
                      <div className="mt-1 flex items-center space-x-2">
                        <svg
                          className="animate-spin h-5 w-5 text-blue-600"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span className="text-xs text-gray-600">
                          Loading banks...
                        </span>
                      </div>
                    ) : (
                      <select
                        name="bank_id"
                        value={formData.bank_id}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        disabled={isFormLocked}
                      >
                        <option value="">Select Bank</option>
                        {banks.map((bank) => (
                          <option key={bank.bank_id} value={bank.bank_id}>
                            {bank.bank_name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Bank Branch
                    </label>
                    <input
                      type="text"
                      name="bank_branch"
                      value={formData.bank_branch}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter bank branch name"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Bank Charge
                    </label>
                    <input
                      type="text"
                      name="bank_charge"
                      value={formData.bank_charge}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter bank charge"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Bank Details Upload (MEF)
                    </label>
                    <input
                      type="file"
                      name="mef"
                      onChange={handleMefUpload}
                      accept=".pdf,.jpg,.png"
                      data-error-key="mef_upload_id"
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs"
                      disabled={isFormLocked}
                    />
                    {formData.mef_file_name && (
                      <div className="mt-2 flex items-center text-xs text-gray-600">
                        <DocumentIcon className="h-5 w-5 mr-1" />
                        <span>{formData.mef_file_name}</span>
                        <a
                          href={formData.mef_signed_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-blue-600 hover:underline"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </a>
                        <button
                          type="button"
                          onClick={handleRegenerateMefUrl}
                          className="ml-2 flex items-center space-x-1 bg-gray-600 text-white px-2 py-1 rounded-md hover:bg-gray-700 transition-colors text-xs"
                          disabled={!isMefUrlExpired() || isFormLocked}
                        >
                          <ArrowPathIcon className="h-5 w-5" />
                          <span>Regenerate URL</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Bank Proof
                      {providerBankChanged() && (
                        <span className="text-red-600 ml-1">*</span>
                      )}
                    </label>
                    <input
                      type="file"
                      name="bank_proof"
                      onChange={handleBankProofUpload}
                      accept=".pdf,.jpg,.jpeg,.png,.bmp,.doc,.docx,.xls,.xlsx,.csv"
                      data-error-key="bank_proof_upload_id"
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs"
                      disabled={isFormLocked}
                    />
                    {formData.bank_proof_file_name && (
                      <div className="mt-2 flex items-center text-xs text-gray-600">
                        <DocumentIcon className="h-5 w-5 mr-1" />
                        <span>{formData.bank_proof_file_name}</span>
                        <a
                          href={formData.bank_proof_signed_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-blue-600 hover:underline"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                <h3 className="mt-4 text-sm font-semibold text-gray-900">
                  Payment Terms
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Payment Terms
                    </label>
                    <select
                      name="payment_terms"
                      value={formData.payment_terms}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      disabled={isFormLocked}
                    >
                      <option value="30">30 Days</option>
                      <option value="15">15 Days</option>
                      <option value="10">10 Days</option>
                      <option value="7">7 Days</option>
                      <option value="3">3 Days</option>
                      <option value="1">24 hours</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Submission of Claims (Stale)
                    </label>
                    <select
                      name="claims_submission"
                      value={formData.claims_submission}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      disabled={isFormLocked}
                    >
                      <option value="7">7 days</option>
                      <option value="15">15 days</option>
                      <option value="30">30 days</option>
                      <option value="45">45 days</option>
                      <option value="60">60 days</option>
                      <option value="90">90 days</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Submission of Claims (Contract)
                    </label>
                    <select
                      name="claims_submission2"
                      value={formData.claims_submission2}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      disabled={isFormLocked}
                    >
                      <option value="7">7 days</option>
                      <option value="15">15 days</option>
                      <option value="30">30 days</option>
                      <option value="45">45 days</option>
                      <option value="60">60 days</option>
                      <option value="90">90 days</option>
                    </select>
                  </div>
                  <div>
                    <label className="flex items-center gap-1 text-xs font-medium text-gray-700">
                      <span>Prompt Payment</span>
                      <PpdHelpButton
                        helpKey="prompt_payment"
                        onClick={setPpdHelpContent}
                      />
                    </label>
                    <div className="mt-1 flex space-x-4">
                      <label className="flex items-center text-xs">
                        <input
                          type="radio"
                          name="ppd_tag"
                          value={true}
                          checked={formData.ppd_tag === true}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        Yes
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="radio"
                          name="ppd_tag"
                          value={false}
                          checked={formData.ppd_tag === false}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        No
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="flex items-center gap-1 text-xs font-medium text-gray-700">
                      <span>
                        PPD Rate (0.5 - 20)
                        {formData.ppd_tag && (
                          <span className="text-red-500"> *</span>
                        )}
                      </span>
                      <PpdHelpButton
                        helpKey="ppd_rate"
                        onClick={setPpdHelpContent}
                      />
                    </label>
                    <input
                      type="text"
                      name="ppd_rate"
                      value={formData.ppd_rate}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        errors.ppd_rate ? "border-red-500" : "border-gray-200"
                      } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                      placeholder="Enter PPD Rate"
                      disabled={!formData.ppd_tag || isFormLocked}
                      aria-invalid={!!errors.ppd_rate}
                    />
                    {errors.ppd_rate && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.ppd_rate[0]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="flex items-center gap-1 text-xs font-medium text-gray-700">
                      <span>
                        PPD Term
                        {formData.ppd_tag && (
                          <span className="text-red-500"> *</span>
                        )}
                      </span>
                      <PpdHelpButton
                        helpKey="ppd_term"
                        onClick={setPpdHelpContent}
                      />
                    </label>
                    <select
                      name="ppd_term"
                      value={formData.ppd_term}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        errors.ppd_term ? "border-red-500" : "border-gray-200"
                      } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                      disabled={!formData.ppd_tag || isFormLocked}
                      aria-invalid={!!errors.ppd_term}
                    >
                      <option value="0"></option>
                      <option value="1">Outright</option>
                      <option value="2">Rebate</option>
                    </select>
                    {errors.ppd_term && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.ppd_term[0]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Cash Bond
                    </label>
                    <input
                      type="text"
                      name="cash_bond"
                      value={formData.cash_bond}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter Cash Bond"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Interest for Late Payment
                    </label>
                    <input
                      type="text"
                      name="late_payment_int"
                      value={formData.late_payment_int}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter Interest for late payment"
                      disabled={isFormLocked}
                    />
                  </div>
                </div>

                <h3 className="mt-4 text-sm font-semibold text-gray-900">
                  Provider Set-up
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      In-patient/OPD-OR
                    </label>
                    <div className="mt-1 space-y-2">
                      <div className="flex items-center">
                        <label className="flex items-center text-xs mr-2">
                          <input
                            type="checkbox"
                            name="ip_whole_setup"
                            checked={formData.ip_whole_setup !== "0"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                ip_whole_setup: e.target.checked ? "1" : "0",
                              })
                            }
                            className="mr-1"
                            disabled={isFormLocked}
                          />
                          Whole
                        </label>
                        <select
                          name="ip_whole_setup"
                          value={formData.ip_whole_setup}
                          onChange={handleInputChange}
                          className="block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                          disabled={
                            formData.ip_whole_setup === "0" || isFormLocked
                          }
                        >
                          <option value="0"></option>
                          <option value="1">Bank Deposit</option>
                          <option value="2">Check Pick-up</option>
                          <option value="3">Check Delivery</option>
                          <option value="4">Auto Credit</option>
                        </select>
                      </div>
                      <div className="flex items-center">
                        <label className="flex items-center text-xs mr-2">
                          <input
                            type="checkbox"
                            name="ip_split_hospital"
                            checked={formData.ip_split_hospital !== "0"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                ip_split_hospital: e.target.checked ? "1" : "0",
                              })
                            }
                            className="mr-1"
                            disabled={isFormLocked}
                          />
                          Split - HB to Hospital
                        </label>
                        <select
                          name="ip_split_hospital"
                          value={formData.ip_split_hospital}
                          onChange={handleInputChange}
                          className="block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                          disabled={
                            formData.ip_split_hospital === "0" || isFormLocked
                          }
                        >
                          <option value="0"></option>
                          <option value="1">Bank Deposit</option>
                          <option value="2">Check Pick-up</option>
                          <option value="3">Check Delivery</option>
                          <option value="4">Auto Credit</option>
                        </select>
                      </div>
                      <div className="flex items-center">
                        <label className="flex items-center text-xs mr-2">
                          <input
                            type="checkbox"
                            name="ip_split_doctor"
                            checked={formData.ip_split_doctor !== "0"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                ip_split_doctor: e.target.checked ? "1" : "0",
                              })
                            }
                            className="mr-1"
                            disabled={isFormLocked}
                          />
                          Split - PF to Doctor
                        </label>
                        <select
                          name="ip_split_doctor"
                          value={formData.ip_split_doctor}
                          onChange={handleInputChange}
                          className="block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                          disabled={
                            formData.ip_split_doctor === "0" || isFormLocked
                          }
                        >
                          <option value="0"></option>
                          <option value="1">Bank Deposit</option>
                          <option value="2">Check Pick-up</option>
                          <option value="3">Check Delivery</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Emergency
                    </label>
                    <div className="mt-1">
                      <div className="flex items-center">
                        <label className="flex items-center text-xs mr-2">
                          Paid to Hospital
                        </label>
                        <select
                          name="emergency_whole_setup"
                          value={formData.emergency_whole_setup}
                          onChange={handleInputChange}
                          className="block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                          disabled={isFormLocked}
                        >
                          <option value="0"></option>
                          <option value="1">Bank Deposit</option>
                          <option value="2">Check Pick-up</option>
                          <option value="3">Check Delivery</option>
                          <option value="4">Auto Credit</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      OP-Consult/Referral
                    </label>
                    <div className="mt-1 space-y-2">
                      <div className="flex items-center">
                        <label className="flex items-center text-xs mr-2">
                          <input
                            type="checkbox"
                            name="op_hospital_setup"
                            checked={formData.op_hospital_setup !== "0"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                op_hospital_setup: e.target.checked ? "1" : "0",
                              })
                            }
                            className="mr-1"
                            disabled={isFormLocked}
                          />
                          Paid to Hospital
                        </label>
                        <select
                          name="op_hospital_setup"
                          value={formData.op_hospital_setup}
                          onChange={handleInputChange}
                          className="block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                          disabled={
                            formData.op_hospital_setup === "0" || isFormLocked
                          }
                        >
                          <option value="0"></option>
                          <option value="1">Bank Deposit</option>
                          <option value="2">Check Pick-up</option>
                          <option value="3">Check Delivery</option>
                          <option value="4">Auto Credit</option>
                        </select>
                      </div>
                      <div className="flex items-center">
                        <label className="flex items-center text-xs mr-2">
                          <input
                            type="checkbox"
                            name="op_doctor_setup"
                            checked={formData.op_doctor_setup !== "0"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                op_doctor_setup: e.target.checked ? "1" : "0",
                              })
                            }
                            className="mr-1"
                            disabled={isFormLocked}
                          />
                          Paid to Doctor
                        </label>
                        <select
                          name="op_doctor_setup"
                          value={formData.op_doctor_setup}
                          onChange={handleInputChange}
                          className="block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                          disabled={
                            formData.op_doctor_setup === "0" || isFormLocked
                          }
                        >
                          <option value="0"></option>
                          <option value="1">Bank Deposit</option>
                          <option value="2">Check Pick-up</option>
                          <option value="3">Check Delivery</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      OP-Lab/APE/Facility Fee
                    </label>
                    <div className="mt-1">
                      <div className="flex items-center">
                        <label className="flex items-center text-xs mr-2">
                          Paid to Hospital
                        </label>
                        <select
                          name="lab_hospital_setup"
                          value={formData.lab_hospital_setup}
                          onChange={handleInputChange}
                          className="block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                          disabled={isFormLocked}
                        >
                          <option value="0"></option>
                          <option value="1">Bank Deposit</option>
                          <option value="2">Check Pick-up</option>
                          <option value="3">Check Delivery</option>
                          <option value="4">Auto Credit</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="block text-xs font-medium text-gray-700">
                        Clinic Setting
                      </label>
                      <ClinicSettingExtractClaimsButton
                        compact
                        onClick={() => setShowClinicSettingExtractClaimsInfo(true)}
                      />
                    </div>
                    <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-4 text-amber-900">
                      Extract Claims may override this Clinic Setting payee setup based on saved CPT and physician claim amounts. Settings remain editable for future specs.
                    </div>
                    <div className="mt-1 space-y-2">
                      <div className="flex items-center">
                        <label className="flex items-center text-xs mr-2">
                          <input
                            type="checkbox"
                            name="clinic_hospital_setup"
                            checked={formData.clinic_hospital_setup !== "0"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                clinic_hospital_setup: e.target.checked
                                  ? "1"
                                  : "0",
                              })
                            }
                            className="mr-1"
                            disabled={isFormLocked}
                          />
                          Paid to Hospital
                        </label>
                        <select
                          name="clinic_hospital_setup"
                          value={formData.clinic_hospital_setup}
                          onChange={handleInputChange}
                          className="block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                          disabled={
                            formData.clinic_hospital_setup === "0" || isFormLocked
                          }
                        >
                          <option value="0"></option>
                          <option value="1">Bank Deposit</option>
                          <option value="2">Check Pick-up</option>
                          <option value="3">Check Delivery</option>
                          <option value="4">Auto Credit</option>
                        </select>
                      </div>
                      <div className="flex items-center">
                        <label className="flex items-center text-xs mr-2">
                          <input
                            type="checkbox"
                            name="clinic_doctor_setup"
                            checked={formData.clinic_doctor_setup !== "0"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                clinic_doctor_setup: e.target.checked
                                  ? "1"
                                  : "0",
                              })
                            }
                            className="mr-1"
                            disabled={isFormLocked}
                          />
                          Paid to Doctor
                        </label>
                        <select
                          name="clinic_doctor_setup"
                          value={formData.clinic_doctor_setup}
                          onChange={handleInputChange}
                          className="block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                          disabled={
                            formData.clinic_doctor_setup === "0" || isFormLocked
                          }
                        >
                          <option value="0"></option>
                          <option value="1">Bank Deposit</option>
                          <option value="2">Check Pick-up</option>
                          <option value="3">Check Delivery</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="mt-4 text-sm font-semibold text-gray-900">
                  If Check Delivery
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Contact Person
                    </label>
                    <input
                      type="text"
                      name="checkdel_contperson"
                      value={formData.checkdel_contperson}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter contact person"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Contact No
                    </label>
                    <input
                      type="text"
                      name="checkdel_contno"
                      value={formData.checkdel_contno}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter contact number/s"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Department
                    </label>
                    <input
                      type="text"
                      name="checkdel_dep"
                      value={formData.checkdel_dep}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter department"
                      disabled={isFormLocked}
                    />
                  </div>
                </div>

                <h3 className="mt-4 text-sm font-semibold text-gray-900">
                  Professional Fees
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      With Medical Coordinator (MC)?
                    </label>
                    <div className="mt-1 flex space-x-4">
                      <label className="flex items-center text-xs">
                        <input
                          type="radio"
                          name="mc_flag"
                          value={true}
                          checked={formData.mc_flag === true}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        Yes
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="radio"
                          name="mc_flag"
                          value={false}
                          checked={formData.mc_flag === false}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        No
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      MC Fee
                    </label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <input
                        type="text"
                        name="mc_ip_fee"
                        value={formData.mc_ip_fee}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        placeholder="Enter IP amount"
                        disabled={isFormLocked}
                      />
                      <input
                        type="text"
                        name="mc_op_fee"
                        value={formData.mc_op_fee}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                        placeholder="Enter OP amount"
                        disabled={isFormLocked}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Doctor's Name
                    </label>
                    <input
                      type="text"
                      name="mc_doctor_name"
                      value={formData.mc_doctor_name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter doctor's name"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Specialization
                    </label>
                    <input
                      type="text"
                      name="mc_specialization"
                      value={formData.mc_specialization}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter specialization"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Clinic Hours
                    </label>
                    <input
                      type="text"
                      name="mc_clinic_hours"
                      value={formData.mc_clinic_hours}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter clinic hours"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Room Number
                    </label>
                    <input
                      type="text"
                      name="mc_room_no"
                      value={formData.mc_room_no}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter room number"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Secretary Name
                    </label>
                    <input
                      type="text"
                      name="mc_secretary_name"
                      value={formData.mc_secretary_name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter secretary name"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Contact Number
                    </label>
                    <input
                      type="text"
                      name="mc_contact_no"
                      value={formData.mc_contact_no}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter contact number"
                      disabled={isFormLocked}
                    />
                  </div>
                </div>

                <h3 className="mt-4 text-sm font-semibold text-gray-900">
                  In-patient PF Rates
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Ward
                    </label>
                    <input
                      type="text"
                      name="ip_ward_rate"
                      value={formData.ip_ward_rate}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter ward amount"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Semi-Private
                    </label>
                    <input
                      type="text"
                      name="ip_semi_private_rate"
                      value={formData.ip_semi_private_rate}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter semi-private amount"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Private
                    </label>
                    <input
                      type="text"
                      name="ip_private_rate"
                      value={formData.ip_private_rate}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter private amount"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Suite
                    </label>
                    <input
                      type="text"
                      name="ip_suite_rate"
                      value={formData.ip_suite_rate}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter suite amount"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      ICU
                    </label>
                    <input
                      type="text"
                      name="ip_icu_rate"
                      value={formData.ip_icu_rate}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter ICU amount"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Anes
                    </label>
                    <input
                      type="text"
                      name="ip_anes_rate"
                      value={formData.ip_anes_rate}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter anes amount"
                      disabled={isFormLocked}
                    />
                  </div>
                </div>

                <h3 className="mt-4 text-sm font-semibold text-gray-900">
                  Out-patient PF Rates
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      PCP
                    </label>
                    <input
                      type="text"
                      name="op_pcp_rate"
                      value={formData.op_pcp_rate}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter PCP amount"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Specialist
                    </label>
                    <input
                      type="text"
                      name="op_specialist_rate"
                      value={formData.op_specialist_rate}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter specialist amount"
                      disabled={isFormLocked}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "signatories" && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Signatory Name 1
                    </label>
                    <input
                      type="text"
                      name="signatories1"
                      value={formData.signatories1}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter signatory name"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Designation 1
                    </label>
                    <input
                      type="text"
                      name="designation1"
                      value={formData.designation1}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter designation"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Signatory Name 2
                    </label>
                    <input
                      type="text"
                      name="signatories2"
                      value={formData.signatories2}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter signatory name"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Designation 2
                    </label>
                    <input
                      type="text"
                      name="designation2"
                      value={formData.designation2}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter designation"
                      disabled={isFormLocked}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "internet_connectivity" && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Hospital Internet Access
                    </label>
                    <div className="mt-1 space-y-2">
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          name="internet_industrial_dept"
                          checked={formData.internet_industrial_dept}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        Industrial Dept.
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          name="internet_billing_dept"
                          checked={formData.internet_billing_dept}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        Billing Dept.
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          name="internet_emergency"
                          checked={formData.internet_emergency}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        Emergency
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Areas with Free Wifi
                    </label>
                    <div className="mt-1 space-y-2">
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          name="wifi_industrial_hmo"
                          checked={formData.wifi_industrial_hmo}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        Industrial/HMO Dept.
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          name="wifi_ip_rooms"
                          checked={formData.wifi_ip_rooms}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        Hospital In-Patient Rooms
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          name="wifi_emergency"
                          checked={formData.wifi_emergency}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        Emergency
                      </label>
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          name="wifi_mab"
                          checked={formData.wifi_mab}
                          onChange={handleInputChange}
                          className="mr-1"
                          disabled={isFormLocked}
                        />
                        MAB
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "miscellaneous" && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Payment Set-up
                    </label>
                    <select
                      name="payment_setup"
                      value={formData.payment_setup}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      disabled={isFormLocked}
                    >
                      <option value="0"></option>
                      <option value="1">Blanket</option>
                      <option value="2">Non-Blanket</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Doctor Accreditation
                    </label>
                    <select
                      name="exclusive_tag"
                      value={formData.exclusive_tag}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      disabled={isFormLocked}
                    >
                      <option value="0"></option>
                      <option value="1">Umbrella</option>
                      <option value="2">Non-Umbrella</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Accreditation Date
                    </label>
                    <input
                      type="date"
                      name="accreditation_date"
                      value={formData.accreditation_date}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        errors.accreditation_date ? "border-red-500" : "border-gray-200"
                      } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                      placeholder="Enter accreditation date (yyyy-mm-dd)"
                      disabled={isFormLocked}
                    />
                    {errors.accreditation_date && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.accreditation_date[0]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Disaccreditation Date
                    </label>
                    <input
                      type="date"
                      name="disaccreditation_date"
                      value={formData.disaccreditation_date}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        errors.disaccreditation_date ? "border-red-500" : "border-gray-200"
                      } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                      placeholder="Enter disaccreditation date (yyyy-mm-dd)"
                      disabled={isFormLocked}
                    />
                    {errors.disaccreditation_date && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.disaccreditation_date[0]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Mode of Releasing
                    </label>
                    <select
                      name="mode_of_release"
                      value={formData.mode_of_release}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      disabled={isFormLocked}
                    >
                      <option value="0">ADA</option>
                      <option value="1">Courier</option>
                      <option value="3">Pick-up</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Business Hours
                    </label>
                    <input
                      type="text"
                      name="business_hours"
                      value={formData.business_hours}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter business hours"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Credit Limit
                    </label>
                    <input
                      type="text"
                      name="credit_limit"
                      value={formData.credit_limit}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter amount"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Number of Beds
                    </label>
                    <input
                      type="number"
                      name="no_of_bed"
                      value={formData.no_of_bed}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter number of beds"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Orientation Date
                    </label>
                    <input
                      type="date"
                      name="orientation_date"
                      value={formData.orientation_date}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Enter orientation date (yyyy-mm-dd)"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Orientation Contact Person
                    </label>
                    <input
                      type="text"
                      name="orientation_contact_person"
                      value={formData.orientation_contact_person}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                      placeholder="Contact person during orientation"
                      disabled={isFormLocked}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Sign-off Document
                    </label>
                    <input
                      type="file"
                      name="document"
                      onChange={handleDocumentUpload}
                      accept=".pdf,.jpg,.png"
                      data-error-key="document_upload_id"
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs"
                      disabled={isFormLocked}
                    />
                    {formData.document_file_name && (
                      <div className="mt-2 flex items-center text-xs text-gray-600">
                        <DocumentIcon className="h-5 w-5 mr-1" />
                        <span>{formData.document_file_name}</span>
                        <a
                          href={formData.document_signed_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-blue-600 hover:underline"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </a>
                        <button
                          type="button"
                          onClick={handleRegenerateDocumentUrl}
                          className="ml-2 flex items-center space-x-1 bg-gray-600 text-white px-2 py-1 rounded-md hover:bg-gray-700 transition-colors text-xs"
                          disabled={!isDocumentUrlExpired() || isFormLocked}
                        >
                          <ArrowPathIcon className="h-5 w-5" />
                          <span>Regenerate URL</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "physician_affiliation" && (
              <div>
                <div className="mt-4 space-y-4">
                  {!isReadOnlyMode && (
                    <div className="space-y-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                        <div
                          className="lg:col-span-2"
                          data-error-key="physician_affiliations"
                          tabIndex={-1}
                        >
                          <label className="block text-xs font-medium text-gray-700">
                            Select Physician{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <Select
                            inputId="physician_affiliations"
                            options={physicianOptions}
                            value={selectedPhysicianOption}
                            onChange={handlePhysicianSelect}
                            onInputChange={handlePhysicianSearch}
                            placeholder="Search physician..."
                            noOptionsMessage={() =>
                              isPhysicianLoading
                                ? "Loading physicians..."
                                : "No physician found"
                            }
                            isLoading={isPhysicianLoading}
                            isClearable
                            className="mt-1 text-xs"
                            classNamePrefix="select"
                            isDisabled={isFormLocked}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700">
                            Specialization
                          </label>
                          <input
                            type="text"
                            value={
                              physicians.find(
                                (physician) =>
                                  physician.physician_id ===
                                  newPhysicianAffiliation.physician_id
                              )?.specialization ||
                              newPhysicianAffiliation.specialization ||
                              ""
                            }
                            className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs bg-gray-100 cursor-not-allowed"
                            placeholder="Select a physician"
                            readOnly
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700">
                            Sub Specialization
                          </label>
                          <input
                            type="text"
                            value={
                              physicians.find(
                                (physician) =>
                                  physician.physician_id ===
                                  newPhysicianAffiliation.physician_id
                              )?.sub_specialization ||
                              newPhysicianAffiliation.sub_specialization ||
                              ""
                            }
                            className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs bg-gray-100 cursor-not-allowed"
                            placeholder="Select a physician"
                            readOnly
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700">
                            Room Number
                          </label>
                          <input
                            type="text"
                            name="room_no"
                            value={newPhysicianAffiliation.room_no}
                            onChange={handleNewPhysicianAffiliationChange}
                            className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                            placeholder="Enter room number"
                            disabled={isFormLocked}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700">
                            Schedule
                          </label>
                          <input
                            type="text"
                            name="schedule"
                            value={newPhysicianAffiliation.schedule}
                            onChange={handleNewPhysicianAffiliationChange}
                            className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                            placeholder="Enter schedule"
                            disabled={isFormLocked}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">
                          Remarks
                        </label>
                        <textarea
                          name="remarks"
                          value={newPhysicianAffiliation.remarks}
                          onChange={handleNewPhysicianAffiliationChange}
                          className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                          rows={2}
                          placeholder="Enter remarks"
                          disabled={isFormLocked}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={addPhysicianAffiliation}
                        className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors text-xs disabled:opacity-50"
                        disabled={isFormLocked}
                      >
                        <PlusIcon className="h-5 w-5" />
                        <span>Add Physician</span>
                      </button>
                    </div>
                  )}

                  <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
                    <div className="flex items-center space-x-2">
                      <label className="text-xs font-medium text-gray-700">
                        Search:
                      </label>
                      <div className="relative">
                        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                        <input
                          type="text"
                          value={physicianAffiliationSearchQuery}
                          onChange={(e) => {
                            setPhysicianAffiliationSearchQuery(e.target.value);
                            setPhysicianAffiliationCurrentPage(1);
                          }}
                          className="pl-7 pr-3 py-1.5 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Search physician affiliation"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center space-x-2">
                        <label className="text-xs font-medium text-gray-700">
                          Show entries:
                        </label>
                        <select
                          value={physicianAffiliationItemsPerPage}
                          onChange={handlePhysicianAffiliationItemsPerPageChange}
                          className="border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          disabled={isLoading}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                      {providerCode && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handleExportProviderTabData(
                                "physician-affiliations",
                                "csv",
                                physicianAffiliationSearchQuery.trim()
                              )
                            }
                            className="flex items-center gap-1 bg-emerald-600 text-white px-3 py-1.5 rounded-md hover:bg-emerald-700 transition-colors text-xs disabled:opacity-50"
                            disabled={isExportingPhysician || isLoading}
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            <span>Export CSV</span>
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleExportProviderTabData(
                                "physician-affiliations",
                                "pdf",
                                physicianAffiliationSearchQuery.trim()
                              )
                            }
                            className="flex items-center gap-1 bg-rose-600 text-white px-3 py-1.5 rounded-md hover:bg-rose-700 transition-colors text-xs disabled:opacity-50"
                            disabled={isExportingPhysician || isLoading}
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            <span>Export PDF</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Physician
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Physician ID
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Specialization
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Sub Specialization
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Room Number
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Schedule
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Remarks
                          </th>
                          {!isReadOnlyMode && (
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {physicianAffiliationPaginatedRows.map((affiliation) => (
                          <tr key={affiliation.rowIndex}>
                            <td className="px-3 py-2 text-xs text-gray-900 align-top">
                              {affiliation.physician_name || "N/A"}
                            </td>
                            <td
                              className="px-3 py-2 text-xs text-gray-900 align-top"
                              data-error-key={`physician_affiliations.${affiliation.rowIndex}.physician_id`}
                              tabIndex={-1}
                            >
                              {affiliation.physician_id || "N/A"}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-900 align-top">
                              {affiliation.specialization || "N/A"}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-900 align-top">
                              {affiliation.sub_specialization || "N/A"}
                            </td>
                            <td
                              className="px-3 py-2 text-xs text-gray-900 align-top"
                              data-error-key={`physician_affiliations.${affiliation.rowIndex}.room_no`}
                              tabIndex={-1}
                            >
                              {affiliation.room_no || "N/A"}
                            </td>
                            <td
                              className="px-3 py-2 text-xs text-gray-900 align-top"
                              data-error-key={`physician_affiliations.${affiliation.rowIndex}.schedule`}
                              tabIndex={-1}
                            >
                              {affiliation.schedule || "N/A"}
                            </td>
                            <td
                              className="px-3 py-2 text-xs text-gray-900 align-top"
                              data-error-key={`physician_affiliations.${affiliation.rowIndex}.remarks`}
                              tabIndex={-1}
                            >
                              {affiliation.remarks || "N/A"}
                            </td>
                            {!isReadOnlyMode && (
                              <td className="px-3 py-2 text-xs align-top">
                                <button
                                  type="button"
                                  onClick={() =>
                                    removePhysicianAffiliation(
                                      affiliation.rowIndex
                                    )
                                  }
                                  className="flex items-center space-x-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                                  disabled={isFormLocked}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                  <span>Remove</span>
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                        {physicianAffiliationPaginatedRows.length === 0 && (
                          <tr>
                            <td
                              colSpan={isReadOnlyMode ? 7 : 8}
                              className="px-4 py-2 text-xs text-gray-500 text-center"
                            >
                              {physicianAffiliationSearchQuery
                                ? "No matching physician affiliations found."
                                : "No physician affiliations added."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div className="text-xs text-gray-600">
                      Showing{" "}
                      {physicianAffiliationTotalItems > 0
                        ? physicianAffiliationStartIndex + 1
                        : 0}{" "}
                      to{" "}
                      {Math.min(
                        physicianAffiliationStartIndex +
                          physicianAffiliationItemsPerPage,
                        physicianAffiliationTotalItems
                      )}{" "}
                      of {physicianAffiliationTotalItems} entries
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() =>
                          handlePhysicianAffiliationPageChange(
                            physicianAffiliationCurrentPage - 1
                          )
                        }
                        disabled={
                          physicianAffiliationCurrentPage === 1 || isLoading
                        }
                        className="px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="px-2 py-1 text-xs text-gray-600">
                        Page {physicianAffiliationCurrentPage} of{" "}
                        {physicianAffiliationTotalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          handlePhysicianAffiliationPageChange(
                            physicianAffiliationCurrentPage + 1
                          )
                        }
                        disabled={
                          physicianAffiliationCurrentPage ===
                            physicianAffiliationTotalPages || isLoading
                        }
                        className="px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "maintenance" && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div data-error-key="maintenance_update_for" tabIndex={-1}>
                    <label className="block text-xs font-medium text-gray-700">
                      Update For <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="maintenance_update_for"
                      value={formData.maintenance_update_for}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        errors.maintenance_update_for
                          ? "border-red-500"
                          : "border-gray-200"
                      } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                      disabled={isFormLocked}
                    >
                      <option value="">Select update for</option>
                      <option value="suspension">Suspension</option>
                      <option value="reactivation">Reactivation</option>
                      <option value="termination">Termination</option>
                    </select>
                    {errors.maintenance_update_for && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.maintenance_update_for[0]}
                      </p>
                    )}
                  </div>

                  <div data-error-key="maintenance_effectivity_date" tabIndex={-1}>
                    <label className="block text-xs font-medium text-gray-700">
                      Effectivity Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="maintenance_effectivity_date"
                      value={formData.maintenance_effectivity_date}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        errors.maintenance_effectivity_date
                          ? "border-red-500"
                          : "border-gray-200"
                      } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                      disabled={isFormLocked}
                    />
                    {errors.maintenance_effectivity_date && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.maintenance_effectivity_date[0]}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2" data-error-key="maintenance_reason" tabIndex={-1}>
                    <label className="block text-xs font-medium text-gray-700">
                      Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="maintenance_reason"
                      value={formData.maintenance_reason}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        errors.maintenance_reason
                          ? "border-red-500"
                          : "border-gray-200"
                      } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                      rows={3}
                      placeholder="Enter reason"
                      disabled={isFormLocked}
                    />
                    {errors.maintenance_reason && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.maintenance_reason[0]}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2" data-error-key="maintenance_remarks" tabIndex={-1}>
                    <label className="block text-xs font-medium text-gray-700">
                      Remarks
                    </label>
                    <textarea
                      name="maintenance_remarks"
                      value={formData.maintenance_remarks}
                      onChange={handleInputChange}
                      className={`mt-1 block w-full border ${
                        errors.maintenance_remarks
                          ? "border-red-500"
                          : "border-gray-200"
                      } rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs`}
                      rows={3}
                      placeholder="Enter remarks"
                      disabled={isFormLocked}
                    />
                    {errors.maintenance_remarks && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.maintenance_remarks[0]}
                      </p>
                    )}
                  </div>
                </div>

                {(formData.maintenance_lastupdate_date ||
                  formData.maintenance_update_by) && (
                  <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md text-xs text-gray-700">
                    <p>
                      <span className="font-medium">Last Updated:</span>{" "}
                      {formData.maintenance_lastupdate_date || "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Updated By:</span>{" "}
                      {formData.maintenance_update_by || "N/A"}
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "cpt" && (
              <div>
                <div className="mt-4 space-y-4">
                  {useProviderCptApi ? (
                    <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-4">
                      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={openProviderCptAddModal}
                            className="inline-flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors text-xs disabled:opacity-50"
                            disabled={isProviderCptLoading || isProviderCptSaving}
                          >
                            <PlusIcon className="h-4 w-4" />
                            <span>Add CPT</span>
                          </button>
                          <button
                            type="button"
                            onClick={handleProviderCptTemplateDownload}
                            className="inline-flex items-center gap-1 bg-emerald-600 text-white px-3 py-1.5 rounded-md hover:bg-emerald-700 transition-colors text-xs disabled:opacity-50"
                            disabled={isProviderCptLoading}
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            <span>Download Template</span>
                          </button>
                          <input
                            ref={providerCptFileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            onChange={handleProviderCptFileChange}
                            className="block max-w-full text-xs text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-gray-700 file:px-3 file:py-1.5 file:text-xs file:text-white hover:file:bg-gray-800"
                            disabled={isProviderCptPreviewing || isProviderCptCommitting}
                          />
                          <button
                            type="button"
                            onClick={handleProviderCptPreviewBatch}
                            className="inline-flex items-center gap-1 bg-amber-600 text-white px-3 py-1.5 rounded-md hover:bg-amber-700 transition-colors text-xs disabled:opacity-50"
                            disabled={
                              !providerCptFile ||
                              isProviderCptPreviewing ||
                              isProviderCptCommitting
                            }
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            <span>{isProviderCptPreviewing ? "Previewing..." : "Preview Batch"}</span>
                          </button>
                          {providerCptFile && (
                            <button
                              type="button"
                              onClick={clearProviderCptFile}
                              className="inline-flex items-center gap-1 border border-gray-200 bg-white px-3 py-1.5 rounded-md text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                              disabled={isProviderCptPreviewing || isProviderCptCommitting}
                            >
                              <XMarkIcon className="h-4 w-4" />
                              <span>Clear</span>
                            </button>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                          <div className="flex items-center space-x-2">
                            <label className="text-xs font-medium text-gray-700">
                              Search:
                            </label>
                            <div className="relative">
                              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                              <input
                                type="text"
                                value={cptSearchQuery}
                                onChange={(e) => {
                                  setCptSearchQuery(e.target.value);
                                  setCptCurrentPage(1);
                                }}
                                className="pl-7 pr-3 py-1.5 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                placeholder="Search CPT list"
                                disabled={isProviderCptLoading}
                              />
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <label className="text-xs font-medium text-gray-700">
                              Show entries:
                            </label>
                            <select
                              value={cptItemsPerPage}
                              onChange={handleCptItemsPerPageChange}
                              className="border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={isProviderCptLoading}
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {providerCptFile && (
                        <p className="text-xs text-gray-600 break-all">
                          Selected file: {providerCptFile.name}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      {!isReadOnlyMode && (
                        <div className="space-y-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div
                              className="lg:col-span-2"
                              data-error-key="cpt_rates"
                              tabIndex={-1}
                            >
                              <label className="block text-xs font-medium text-gray-700">
                                CPT <span className="text-red-500">*</span>
                              </label>
                              <Select
                                inputId="cpt_rates"
                                options={cptSelectOptions}
                                value={selectedCptOption}
                                onChange={handleCptSelect}
                                onInputChange={handleCptSearch}
                                placeholder="Search CPT code or description..."
                                noOptionsMessage={() =>
                                  isCptLoading ? "Loading CPT codes..." : "No CPT found"
                                }
                                isLoading={isCptLoading}
                                isClearable
                                className="mt-1 text-xs"
                                classNamePrefix="select"
                                isDisabled={isFormLocked}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700">
                                Provider Rate
                              </label>
                              <input
                                type="number"
                                name="cpt_rate"
                                value={newCptRate.cpt_rate}
                                onChange={handleNewCptRateChange}
                                className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                                placeholder="Enter provider rate"
                                disabled={isFormLocked}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700">
                                Provider CPT Code
                              </label>
                              <input
                                type="text"
                                name="provider_cpt_code"
                                value={newCptRate.provider_cpt_code}
                                onChange={handleNewCptRateChange}
                                className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                                placeholder="Enter provider CPT code"
                                disabled={isFormLocked}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">
                              CPT Description
                            </label>
                            <input
                              type="text"
                              value={newCptRate.cpt_description || ""}
                              className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs bg-gray-100 cursor-not-allowed"
                              placeholder="Select a CPT code"
                              readOnly
                            />
                          </div>
                          <button
                            type="button"
                            onClick={addCptRate}
                            className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors text-xs disabled:opacity-50"
                            disabled={isFormLocked}
                          >
                            <PlusIcon className="h-5 w-5" />
                            <span>Add Rate</span>
                          </button>
                        </div>
                      )}

                      <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
                        <div className="flex items-center space-x-2">
                          <label className="text-xs font-medium text-gray-700">
                            Search:
                          </label>
                          <div className="relative">
                            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                            <input
                              type="text"
                              value={cptSearchQuery}
                              onChange={(e) => {
                                setCptSearchQuery(e.target.value);
                                setCptCurrentPage(1);
                              }}
                              className="pl-7 pr-3 py-1.5 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Search CPT rates"
                              disabled={isLoading}
                            />
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="text-xs font-medium text-gray-700">
                            Show entries:
                          </label>
                          <select
                            value={cptItemsPerPage}
                            onChange={handleCptItemsPerPageChange}
                            className="border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            disabled={isLoading}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-200">
                      <thead className="bg-gray-50">
                        {useProviderCptApi ? (
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Master CPT Code
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Master CPT Description
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Provider CPT Code
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Provider CPT Description
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              CPT Rate
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        ) : (
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              CPT Code
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              CPT Description
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Provider Rate
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Provider CPT Code
                            </th>
                            {!isReadOnlyMode && (
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            )}
                          </tr>
                        )}
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {isProviderCptLoading ? (
                          <tr>
                            <td
                              colSpan={useProviderCptApi ? 6 : isReadOnlyMode ? 4 : 5}
                              className="px-4 py-4 text-xs text-gray-500 text-center"
                            >
                              Loading CPT list...
                            </td>
                          </tr>
                        ) : (
                          paginatedCptRateRows.map((cptRate) => (
                            <tr key={cptRate.rate_id || `${cptRate.master_cpt_code || cptRate.cpt_code}-${cptRate.rowIndex}`}>
                              {useProviderCptApi ? (
                                <>
                                  <td className="px-3 py-2 text-xs text-gray-900 align-top">
                                    {cptRate.master_cpt_code || "N/A"}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 align-top">
                                    {cptRate.master_cpt_desc || "N/A"}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 align-top">
                                    {cptRate.provider_cpt_code || "N/A"}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 align-top">
                                    {cptRate.cpt_desc || "N/A"}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 align-top">
                                    {cptRate.cpt_rate || "N/A"}
                                  </td>
                                  <td className="px-3 py-2 text-xs align-top">
                                    <button
                                      type="button"
                                      onClick={() => openProviderCptEditModal(cptRate)}
                                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                      disabled={isProviderCptLoading || isProviderCptSaving}
                                      title="Edit provider CPT"
                                    >
                                      <PencilSquareIcon className="h-4 w-4" />
                                      <span>Edit</span>
                                    </button>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td
                                    className="px-3 py-2 text-xs text-gray-900 align-top"
                                    data-error-key={`cpt_rates.${cptRate.rowIndex}.cpt_code`}
                                    tabIndex={-1}
                                  >
                                    {cptRate.cpt_code || "N/A"}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 align-top">
                                    {cptRate.cpt_description || "N/A"}
                                  </td>
                                  <td
                                    className="px-3 py-2 text-xs text-gray-900 align-top"
                                    data-error-key={`cpt_rates.${cptRate.rowIndex}.cpt_rate`}
                                    tabIndex={-1}
                                  >
                                    {cptRate.cpt_rate || "N/A"}
                                  </td>
                                  <td
                                    className="px-3 py-2 text-xs text-gray-900 align-top"
                                    data-error-key={`cpt_rates.${cptRate.rowIndex}.provider_cpt_code`}
                                    tabIndex={-1}
                                  >
                                    {cptRate.provider_cpt_code || "N/A"}
                                  </td>
                                  {!isReadOnlyMode && (
                                    <td className="px-3 py-2 text-xs align-top">
                                      <button
                                        type="button"
                                        onClick={() => removeCptRate(cptRate.rowIndex)}
                                        className="flex items-center space-x-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                                        disabled={isFormLocked}
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                        <span>Remove</span>
                                      </button>
                                    </td>
                                  )}
                                </>
                              )}
                            </tr>
                          ))
                        )}
                        {!isProviderCptLoading && paginatedCptRateRows.length === 0 && (
                          <tr>
                            <td
                              colSpan={useProviderCptApi ? 6 : isReadOnlyMode ? 4 : 5}
                              className="px-4 py-2 text-xs text-gray-500 text-center"
                            >
                              {cptSearchQuery
                                ? "No matching CPT rates found."
                                : "No CPT rates added."}
                              </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div className="text-xs text-gray-600">
                      Showing {totalCptItems > 0 ? cptStartIndex + 1 : 0} to{" "}
                      {Math.min(cptStartIndex + cptItemsPerPage, totalCptItems)}{" "}
                      of {totalCptItems} entries
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => handleCptPageChange(cptCurrentPage - 1)}
                        disabled={cptCurrentPage === 1 || isLoading || isProviderCptLoading}
                        className="px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="px-2 py-1 text-xs text-gray-600">
                        Page {cptCurrentPage} of {totalCptPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCptPageChange(cptCurrentPage + 1)}
                        disabled={
                          cptCurrentPage === totalCptPages ||
                          isLoading ||
                          isProviderCptLoading
                        }
                        className="px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isReadOnlyMode ? (
              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowDocumentsModal(true)}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors text-xs flex items-center space-x-2 disabled:opacity-50"
                  disabled={!hasProviderDocuments || isLoading}
                  title={
                    hasProviderDocuments
                      ? "Open provider documents"
                      : "No uploaded documents available"
                  }
                >
                  <FolderOpenIcon className="h-4 w-4" />
                  <span>Download Document</span>
                </button>
                <button
                  type="button"
                  onClick={handlePrintProvider}
                  className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 transition-colors text-xs flex items-center space-x-2 disabled:opacity-50"
                  disabled={isLoading}
                >
                  <PrinterIcon className="h-4 w-4" />
                  <span>Print</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/medical/update-provider/${providerCode}`)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-xs flex items-center space-x-2 disabled:opacity-50"
                  disabled={!providerCode || isLoading}
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  <span>Edit</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors text-xs flex items-center space-x-2"
                >
                  <XMarkIcon className="h-4 w-4" />
                  <span>Close</span>
                </button>
              </div>
            ) : (
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowDocumentsModal(true)}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors text-xs flex items-center space-x-2 disabled:opacity-50"
                  disabled={!hasProviderDocuments || isLoading}
                  title={
                    hasProviderDocuments
                      ? "Open provider documents"
                      : "No uploaded documents available"
                  }
                >
                  <FolderOpenIcon className="h-4 w-4" />
                  <span>Download Document</span>
                </button>
                <button
                  type="submit"
                  className="bg-emerald-600 text-white px-4 py-2 rounded-md hover:bg-emerald-700 transition-colors text-xs flex items-center space-x-2 shadow-sm disabled:opacity-50"
                  disabled={isFormLocked}
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>
                        {activeTab === "maintenance"
                          ? "Saving..."
                          : isEditMode
                          ? "Updating..."
                          : "Saving..."}
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-4 w-4" />
                      <span>
                        {activeTab === "maintenance"
                          ? "Save Maintenance"
                          : isEditMode
                          ? "Update"
                          : "Save"}
                      </span>
                    </>
                  )}
                </button>
              </div>
            )}
          </form>
        </div>
      </motion.div>

      {providerCptModalMode && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h2 className="text-sm font-semibold text-gray-900">
                {providerCptModalMode === "add" ? "Add CPT" : "Edit CPT"}
              </h2>
              <button
                type="button"
                onClick={closeProviderCptModal}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                disabled={isProviderCptSaving}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {providerCptModalMode === "edit" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Master CPT Code
                    </label>
                    <input
                      type="text"
                      value={providerCptForm.master_cpt_code}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs bg-gray-100"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      Master CPT Description
                    </label>
                    <input
                      type="text"
                      value={providerCptForm.master_cpt_desc}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs bg-gray-100"
                      readOnly
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700">
                    Provider CPT Code
                  </label>
                  <input
                    type="text"
                    name="provider_cpt_code"
                    value={providerCptForm.provider_cpt_code}
                    onChange={handleProviderCptFormChange}
                    className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                    maxLength={50}
                    disabled={isProviderCptSaving}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="cpt_rate"
                    value={providerCptForm.cpt_rate}
                    onChange={handleProviderCptFormChange}
                    className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                    step="0.01"
                    disabled={isProviderCptSaving}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700">
                  CPT Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="cpt_desc"
                  value={providerCptForm.cpt_desc}
                  onChange={handleProviderCptFormChange}
                  className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                  rows={3}
                  maxLength={200}
                  disabled={isProviderCptSaving}
                />
              </div>
            </div>

            <div className="border-t border-gray-200 px-4 py-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeProviderCptModal}
                className="px-3 py-1.5 rounded-md border border-gray-200 bg-white text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                disabled={isProviderCptSaving}
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => handleSaveProviderCpt()}
                className="px-3 py-1.5 rounded-md bg-blue-600 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={isProviderCptSaving}
              >
                {isProviderCptSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {providerCptPreview && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h2 className="text-sm font-semibold text-gray-900">
                Preview Batch
              </h2>
              <button
                type="button"
                onClick={() => setProviderCptPreview(null)}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                disabled={isProviderCptCommitting}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4 overflow-y-auto max-h-[68vh]">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  ["Read", providerCptPreview.read_count],
                  ["Changed", providerCptPreview.changed_count],
                  ["Insert", providerCptPreview.inserted_count],
                  ["Update", providerCptPreview.updated_count],
                  ["Unchanged", providerCptPreview.unchanged_count],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="border border-gray-200 rounded-md bg-gray-50 px-3 py-2"
                  >
                    <p className="text-[11px] uppercase text-gray-500">{label}</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {value ?? 0}
                    </p>
                  </div>
                ))}
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Operation
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Master CPT Code
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Provider CPT Code
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        CPT Description
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(providerCptPreview.changed_rows || []).map((row) => (
                      <tr key={`${row.row_no}-${row.master_cpt_code}-${row.provider_cpt_code}`}>
                        <td className="px-3 py-2 text-xs text-gray-900 align-top">
                          {row.operation || "N/A"}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-900 align-top">
                          {row.master_cpt_code || "N/A"}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-900 align-top">
                          {row.provider_cpt_code ||
                            row.new_provider_cpt_code ||
                            "N/A"}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-900 align-top">
                          {row.cpt_desc || row.new_cpt_desc || "N/A"}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-900 align-top">
                          {row.cpt_rate || row.new_cpt_rate || "N/A"}
                        </td>
                      </tr>
                    ))}
                    {(providerCptPreview.changed_rows || []).length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-3 py-4 text-xs text-gray-500 text-center"
                        >
                          No batch changes found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border-t border-gray-200 px-4 py-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setProviderCptPreview(null)}
                className="px-3 py-1.5 rounded-md border border-gray-200 bg-white text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                disabled={isProviderCptCommitting}
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleProviderCptCommitBatch}
                className="px-3 py-1.5 rounded-md bg-blue-600 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={
                  isProviderCptCommitting ||
                  !providerCptPreview.preview_id ||
                  Number(providerCptPreview.changed_count || 0) === 0
                }
              >
                {isProviderCptCommitting ? "Committing..." : "Commit Batch"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showClinicSettingExtractClaimsInfo && (
        <ClinicSettingExtractClaimsModal
          onClose={() => setShowClinicSettingExtractClaimsInfo(false)}
        />
      )}

      {ppdHelpContent && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ppd-help-title"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h2
                id="ppd-help-title"
                className="text-sm font-semibold text-gray-900"
              >
                {ppdHelpContent.title}
              </h2>
              <button
                type="button"
                onClick={() => setPpdHelpContent(null)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                aria-label="Close PPD help"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              {ppdHelpContent.points.map((point) => (
                <p key={point} className="text-xs leading-5 text-gray-700">
                  {point}
                </p>
              ))}
            </div>
            <div className="border-t border-gray-200 px-4 py-3 text-right">
              <button
                type="button"
                onClick={() => setPpdHelpContent(null)}
                className="inline-flex items-center px-3 py-1.5 rounded-md bg-blue-600 text-xs font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {showDocumentsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h2 className="text-sm font-semibold text-gray-900">
                Provider Documents
              </h2>
              <button
                type="button"
                onClick={() => setShowDocumentsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto max-h-[70vh]">
              {providerDocumentEntries.map((documentEntry) => {
                const hasVersion1 = !!documentEntry.legacyUrl;
                const hasVersion2 = !!documentEntry.signedUrl;
                const hasAnyVersion = hasVersion1 || hasVersion2;
                const showExpiredBadge =
                  documentEntry.isExpired && hasVersion2 && !hasVersion1;
                const badgeClass = !hasAnyVersion
                  ? "bg-gray-100 text-gray-600"
                  : showExpiredBadge
                  ? "bg-amber-100 text-amber-700"
                  : hasVersion1 && hasVersion2
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-blue-100 text-blue-700";
                const badgeText = !hasAnyVersion
                  ? "No File"
                  : showExpiredBadge
                  ? "Expired URL"
                  : hasVersion1 && hasVersion2
                  ? "V1 + V2"
                  : hasVersion2
                  ? "V2 Available"
                  : "V1 Available";

                return (
                  <div
                    key={documentEntry.key}
                    className="rounded-lg border border-slate-200 bg-slate-50/60 p-4"
                  >
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <h3 className="text-sm font-semibold text-slate-900">
                      {documentEntry.label}
                    </h3>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badgeClass}`}
                    >
                      {badgeText}
                    </span>
                  </div>
                  <p className="text-xs text-gray-700 break-all mb-2">
                    {documentEntry.fileName || "No uploaded file available."}
                  </p>
                  {documentEntry.expiresAt && (
                    <p className="text-[11px] text-gray-500 mb-3">
                      URL expires at: {documentEntry.expiresAt}
                    </p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <a
                      href={documentEntry.signedUrl || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center space-x-1 px-3 py-1.5 rounded-md text-xs ${
                        documentEntry.signedUrl
                          ? "bg-blue-600 text-white hover:bg-blue-700"
                          : "bg-gray-200 text-gray-500 pointer-events-none"
                      }`}
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      <span>View V2.0</span>
                    </a>
                    <a
                      href={documentEntry.legacyUrl || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center space-x-1 px-3 py-1.5 rounded-md text-xs ${
                        documentEntry.legacyUrl
                          ? "bg-indigo-600 text-white hover:bg-indigo-700"
                          : "bg-gray-200 text-gray-500 pointer-events-none"
                      }`}
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      <span>View V1.0</span>
                    </a>
                    <button
                      type="button"
                      onClick={documentEntry.onRegenerate}
                      className="inline-flex items-center space-x-1 px-3 py-1.5 rounded-md text-xs bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-50"
                      disabled={!documentEntry.canRegenerate || isLoading}
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      <span>Regenerate URL</span>
                    </button>
                  </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {showMaintenanceConfirmation && (
        <MaintenanceConfirmationPrompt
          payload={pendingMaintenancePayload}
          onConfirm={async () => {
            const payloadToSave = pendingMaintenancePayload;
            setShowMaintenanceConfirmation(false);
            setPendingMaintenancePayload(null);
            if (!payloadToSave) {
              return;
            }
            await applyMaintenanceUpdate(payloadToSave);
          }}
          onCancel={() => {
            setShowMaintenanceConfirmation(false);
            setPendingMaintenancePayload(null);
          }}
        />
      )}

      {showConfirmation && (
        <ConfirmationPrompt
          onConfirm={() => {
            setShowConfirmation(false);
            setIsFormDirty(false);
            // navigate(nextLocation);
            navigate(-1); // go back to previous
          }}
          onCancel={() => {
            setShowConfirmation(false);
            setPreviousLocation(location.pathname);
          }}
        />
      )}
    </div>
  );
};

export default ProviderEnrollmentForm;
